import EventBus from 'eventing-bus';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Checkbox, CircularProgress, FormControlLabel, Switch } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Container, Row, Col, Card, CardBody, CardHeader, Badge, Table, Button, Input, InputGroup, InputGroupText, Pagination, PaginationItem, PaginationLink, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Nav, NavItem, NavLink, TabContent, TabPane, Progress, Alert, ButtonGroup, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip } from 'reactstrap';

import '../Admin.css';
import UsersManagement from '../Users';
import ExitImpersonate from './ExitImpersonate';
import BulkActions from '../../../components/BulkActions';
import ExportButton from '../../../components/ExportButton';
import WorkspaceSidebar from './WorkspaceSidebar';
import FeedbackSidebar from './FeedbackSidebar';
import { display } from '../../../../node_modules/@mui/system/index';
import NotifyHistory from './NotifyHistory';
import { getAdminWorkspaces, getWorkspaceStats, updateAdminWorkspace, deleteAdminWorkspace, getWorkspaceById, getWorkspaceActivity, exportAdminWorkspaces, impersonateUser, restoreWorkspace, getWorkspacePlans } from '../../../store/Admin';

const WorkspacesManagement = ({ accessType, workspaceId }) => {
  const dispatch = useDispatch();
  const { adminWorkspaces, workspaceStats, workspacesPagination, selectedWorkspaceDetails, selectedWorkspaceActivity, loading, deletedWorkspaces, deletedWorkspacesPagination, workspacePlans, hasStripeConfigured, plansMessage, adminFeedback, feedbackStats, feedbackPagination } = useSelector(state => state['Admin']);

  const [formData, setFormData] = useState({});
  const [modalType, setModalType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationData, setNotificationData] = useState({ subject: '', message: '', selectedWorkspaces: [], selectAll: false });
  const [allWorkspacesData, setAllWorkspacesData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectAll, setSelectAll] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [activeTab, setActiveTab] = useState('workspaces');
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState({});
  const [showNotificationHistorySidebar, setShowNotificationHistorySidebar] = useState(false);

  // Feedback tab states
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState('');
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState('');
  const [feedbackSearchTerm, setFeedbackSearchTerm] = useState('');
  const [feedbackCurrentPage, setFeedbackCurrentPage] = useState(1);
  const [showFeedbackSidebar, setShowFeedbackSidebar] = useState(false);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);

  useEffect(() => {
    dispatch(getWorkspaceStats());
    dispatch(getWorkspacePlans());
  }, []);

  useEffect(() => {
    console.log('*********** workspacePlans = ', workspacePlans);
  }, [workspacePlans]);

  useEffect(() => {
    dispatch(getAdminWorkspaces({ page: currentPage, search: searchTerm, status: statusFilter, plan: planFilter }));
  }, [currentPage, searchTerm, statusFilter, planFilter]);

  // Fetch feedback when feedback tab is active
  useEffect(() => {
    if (activeTab === 'feedback' && accessType !== 'agency') {
      dispatch({
        type: 'GET_ADMIN_FEEDBACK',
        payload: {
          page: feedbackCurrentPage,
          search: feedbackSearchTerm,
          status: feedbackStatusFilter,
          type: feedbackTypeFilter
        }
      });
      dispatch({ type: 'GET_FEEDBACK_STATS' });
    }
  }, [activeTab, feedbackCurrentPage, feedbackSearchTerm, feedbackStatusFilter, feedbackTypeFilter, accessType]);

  // Removed useEffect for restore tab as we're using sidebar now

  const handleFixChildWorkspaces = async () => {
    try {
      const response = await fetch('/api/admin/workspaces/fix-child-workspaces', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        EventBus.publish('success', result.message || 'Child workspaces fixed successfully');
        // Refresh the workspace list
        dispatch({ type: 'GET_ADMIN_WORKSPACES', payload: { page: currentPage, search: searchTerm, status: statusFilter } });
        dispatch({ type: 'GET_WORKSPACE_STATS' });
      } else {
        EventBus.publish('error', result.message || 'Failed to fix child workspaces');
      }
    } catch (error) {
      EventBus.publish('error', 'Error fixing child workspaces');
      console.error('Fix child workspaces error:', error);
    }
  };

  const handleWorkspaceAction = (workspaceId, action, data = {}) => {
    const filters = { page: currentPage, search: searchTerm, status: statusFilter };

    if (action === 'suspend') {
      const workspace = adminWorkspaces.find(w => w._id === workspaceId);
      if (!workspace) return;

      setConfirmData({
        title: 'Pause Workspace',
        message: `Are you sure you want to pause "${workspace.name}"?`,
        details: [
          'Block all member access to the workspace',
          'Stop all agents and automations',
          'Disable API access and integrations',
          'Prevent creation of new deals, contacts, etc.'
        ],
        note: 'The workspace can be reactivated later.',
        confirmText: 'Pause Workspace',
        confirmClass: 'btn-warning',
        onConfirm: () => {
          dispatch({
            type: 'SUSPEND_WORKSPACE',
            payload: { id: workspaceId, reason: 'Admin action', filters }
          });
          setShowConfirmModal(false);
        }
      });
      setShowConfirmModal(true);
      return;
    }
    if (action === 'activate') {
      return dispatch({
        type: 'UNSUSPEND_WORKSPACE',
        payload: { id: workspaceId, filters }
      });
    }
    if (action === 'delete') {
      const workspace = adminWorkspaces.find(w => w._id === workspaceId);
      if (!workspace) return;

      setConfirmData({
        title: 'Deactivate Workspace',
        message: `Are you sure you want to deactivate "${workspace.name}"?`,
        details: [
          'Workspace will be moved to deactivated status',
          'All members will lose access',
          'Can be reactivated later from the main table'
        ],
        note: 'This workspace can be reactivated anytime.',
        confirmText: 'Deactivate Workspace',
        confirmClass: 'btn-danger',
        onConfirm: () => {
          dispatch(deleteAdminWorkspace({ id: workspaceId, filters }));
          setShowConfirmModal(false);
        }
      });
      setShowConfirmModal(true);
      return;
    }
    if (action === 'restore') {
      const workspace = adminWorkspaces.find(w => w._id === workspaceId);
      if (!workspace) return;

      setConfirmData({
        title: 'Reactivate Workspace',
        message: `Are you sure you want to reactivate "${workspace.name}"?`,
        details: [
          'Workspace will become active again',
          'Members will regain access',
          'All data will be restored'
        ],
        note: 'The workspace will be fully functional after reactivation.',
        confirmText: 'Reactivate Workspace',
        confirmClass: 'btn-success',
        onConfirm: () => {
          dispatch(restoreWorkspace({ id: workspaceId, filters }));
          setShowConfirmModal(false);
        }
      });
      setShowConfirmModal(true);
      return;
    }

    if (action === 'changePlan') {
      return dispatch({
        type: 'CHANGE_WORKSPACE_PLAN',
        payload: {
          id: workspaceId,
          planId: data.planId,
          targetPlan: data.targetPlan,
          filters
        }
      });
    }

    if (action === 'resetUsage') {
      return dispatch({
        type: 'RESET_WORKSPACE_USAGE',
        payload: { id: workspaceId, filters }
      });
    }

    // Restore action is now handled in the RestoreWorkspacesSidebar component
  };

  const handleBulkAction = async (action, workspaceIds, data = {}) => {
    const filters = { page: currentPage, search: searchTerm, status: statusFilter };

    switch (action) {
      case 'suspend':
        return dispatch({
          type: 'BULK_WORKSPACE_ACTION',
          payload: { action: 'suspend', workspaceIds, filters }
        });
      case 'activate':
        return dispatch({
          type: 'BULK_WORKSPACE_ACTION',
          payload: { action: 'activate', workspaceIds, filters }
        });
      case 'delete':
        return dispatch({ type: 'BULK_DELETE_WORKSPACES', payload: { ids: workspaceIds, filters } });
      case 'resetUsage':
        return dispatch({
          type: 'BULK_WORKSPACE_ACTION',
          payload: { action: 'resetUsage', workspaceIds, filters }
        });
      case 'upgradePlan':
        // data should contain the selected plan from the input
        return dispatch({
          type: 'BULK_WORKSPACE_ACTION',
          payload: {
            action: 'changePlan',
            workspaceIds,
            planId: data.selectedOption || 'Free',
            filters
          }
        });
      case 'send-notification':
        return dispatch({
          type: 'SEND_WORKSPACE_NOTIFICATION',
          payload: {
            workspaceIds,
            subject: data.subject,
            message: data.message
          }
        });
      default:
        console.warn('Unknown bulk workspace action:', action);
    }

    // Clear selection after action
    setSelectedWorkspaces([]);
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) setSelectedWorkspaces([]);
    else setSelectedWorkspaces(adminWorkspaces.map(workspace => workspace['_id']));
    setSelectAll(!selectAll);
  };

  const handleDeselectAll = () => {
    setSelectedWorkspaces([]);
    setSelectAll(false);
  };

  const handleExportAllWorkspaces = (format) => {
    setExportLoading(true);

    dispatch(exportAdminWorkspaces({
      search: searchTerm,
      status: statusFilter,
      plan: planFilter,
      callback: (exportData, error) => {
        setExportLoading(false);

        if (error) {
          console.error('Export failed:', error);
          return;
        }

        if (!exportData?.workspaces) {
          console.error('No export data received');
          return;
        }

        // Use the exportFormattedData function with all workspaces
        import('../../../utils/exportData').then(({ exportFormattedData }) => {
          const exportFilename = `all-workspaces-${new Date().toISOString().split('T')[0]}`;

          exportFormattedData(
            exportData.workspaces,
            [
              { 'key': 'workspaceName', 'name': 'Workspace Name' },
              { 'key': 'ownerName', 'name': 'Owner' },
              { 'key': 'ownerEmail', 'name': 'Owner Email' },
              { 'key': 'planName', 'name': 'Plan' },
              { 'key': 'statusText', 'name': 'Status' },
              { 'key': 'memberCount', 'name': 'Users' },
              { 'key': 'agentCount', 'name': 'Agents' },
              { 'key': 'createdDate', 'name': 'Created' },
              { 'key': 'lastActivityDate', 'name': 'Last Activity' }
            ],
            format,
            `${exportFilename}.${format === 'excel' ? 'xlsx' : format}`
          );
        });
      }
    }));
  };

  const handleWorkspaceSelect = (workspaceId) => {
    setSelectedWorkspaces(prev => {
      const newSelected = prev.includes(workspaceId)
        ? prev.filter(id => id !== workspaceId)
        : [...prev, workspaceId];

      setSelectAll(newSelected.length === adminWorkspaces.length);
      return newSelected;
    });
  };

  const handleImpersonateWorkspace = (workspace) => {
    if (!workspace || !workspace.createdBy || !workspace.createdBy._id) {
      EventBus.publish('error', 'Cannot impersonate workspace owner: Owner information not available');
      return;
    }

    const owner = workspace.createdBy;

    // Show confirmation modal for impersonate
    setConfirmData({
      title: '⚠️ Security Warning - Workspace Impersonation',
      message: `You are about to impersonate the owner of workspace "${workspace.name}".`,
      details: [
        `Owner: ${owner.firstName} ${owner.lastName} (${owner.email})`,
        'You will be logged in as this user for 2 hours',
        'This action will be recorded in audit logs',
        'You will gain access to their workspace and data',
        'You will be redirected to their dashboard'
      ],
      note: 'This is a powerful administrative action. Use responsibly.',
      confirmText: 'Continue with Impersonation',
      confirmClass: 'btn-warning',
      onConfirm: () => {
        setShowConfirmModal(false);
        dispatch(impersonateUser({
          'id': owner._id,
          'callback': (data) => {
            if (data && data.token) {
              // Store current admin context
              const currentToken = localStorage.getItem('token');
              const adminContext = {
                originalToken: currentToken,
                adminId: data.user._id,
                adminName: `Admin impersonating ${owner.firstName} ${owner.lastName} (Workspace: ${workspace.name})`,
                impersonatedAt: new Date().toISOString(),
                expiresAt: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
                workspaceContext: {
                  workspaceId: workspace._id,
                  workspaceName: workspace.name
                }
              };

              localStorage.setItem('impersonationContext', JSON.stringify(adminContext));
              localStorage.setItem('token', data.token);

              // Show success message and redirect
              EventBus.publish('success', `Successfully impersonating ${owner.firstName} ${owner.lastName}. Redirecting to their workspace...`);

              // Redirect to the user's dashboard after a short delay
              setTimeout(() => {
                window.location.href = '/dashboard';
              }, 1500);
            }
          }
        }));
      }
    });
    setShowConfirmModal(true);
  };

  const workspaceBulkActions = [
    {
      key: 'activate',
      type: 'activate',
      label: 'Activate',
      description: 'Activate selected workspaces',
      requiresConfirmation: true,
      confirmationMessage: 'This will activate all selected workspaces.'
    },
    {
      key: 'suspend',
      type: 'deactivate',
      label: 'Pause',
      description: 'Pause selected workspaces',
      requiresConfirmation: true,
      confirmationMessage: 'This will pause all selected workspaces.',
      warning: 'Paused workspaces will lose access to all features.'
    },
    {
      key: 'delete',
      type: 'delete',
      label: 'Deactivate',
      description: 'Deactivate selected workspaces',
      requiresConfirmation: true,
      confirmationMessage: 'This will deactivate all selected workspaces.',
      warning: 'Deactivated workspaces can be reactivated later.'
    },
    {
      key: 'resetUsage',
      type: 'restart',
      label: 'Reset Usage',
      description: 'Reset usage for selected workspaces',
      requiresConfirmation: true,
      confirmationMessage: 'This will reset call minutes/credits for all selected workspaces.',
      warning: 'Usage data will be reset to plan defaults. This action cannot be undone.',
      withProgress: true
    },
    {
      key: 'upgradePlan',
      type: 'edit',
      label: 'Upgrade Plan',
      description: 'Change plan for selected workspaces',
      requiresConfirmation: true,
      confirmationMessage: 'Select the target plan for all selected workspaces:',
      requiresInput: true,
      inputOptions: [
        { value: 'Free', label: 'Free Plan' },
        { value: 'Starter_business_month', label: 'Starter Plan' }
      ],
      warning: 'This will change the plan for all selected workspaces.',
      withProgress: true
    }
  ];

  const openModal = (type, workspace) => {
    setModalType(type);
    setSelectedWorkspace(workspace || null);

    if (type === 'view' && workspace) {
      // Open sidebar instead of modal for view action
      setShowSidebar(true);
      dispatch(getWorkspaceById({ id: workspace['_id'] }));
      dispatch(getWorkspaceActivity({ id: workspace['_id'], page: 1, limit: 10 }));
    } else {
      // Open modal for other actions (create, edit)
      setShowModal(true);

      if (type === 'create') {
        // Fetch available plans when creating workspace
        dispatch(getWorkspacePlans());
        // Reset form for new workspace creation
        setFormData({
          name: '',
          userEmail: '',
          firstName: '',
          lastName: '',
          description: '',
          status: 'active',
          plan: '',  // Empty by default, will select from available plans
          hideBilling: false,  // New field for hiding billing
          currentPeriodStart: null,
          currentPeriodEnd: null
        });
      } else if (workspace) {
        // Fetch plans for edit modal too
        dispatch(getWorkspacePlans());

        // Get the latest workspace data from Redux store to ensure we have fresh data
        const freshWorkspace = adminWorkspaces.find(w => w._id === workspace._id) || workspace;

        // Edit existing workspace
        const subscription = freshWorkspace['currentSubscription'];
        const isPaymentActive = subscription?.['status'] === 'active' && !subscription?.['isLocked'];

        // Helper function to format date for input type="date"
        const formatDateForInput = (dateValue) => {
          if (!dateValue) return '';
          const date = new Date(dateValue);
          return date.toISOString().split('T')[0];
        };

        setFormData({
          name: freshWorkspace['name'] || '',
          userEmail: freshWorkspace['createdBy']?.['email'] || '',
          firstName: freshWorkspace['createdBy']?.['firstName'] || '',
          lastName: freshWorkspace['createdBy']?.['lastName'] || '',
          description: freshWorkspace['description'] || '',
          status: freshWorkspace['status'] || 'active',
          plan: freshWorkspace['currentSubscription']?.['planId']?.['_id'] || freshWorkspace['currentSubscription']?.['planId'] || '',
          hideBilling: isPaymentActive,
          currentPeriodStart: formatDateForInput(subscription?.['currentPeriodStart']),
          currentPeriodEnd: formatDateForInput(subscription?.['currentPeriodEnd'])
        });
      }
    }
  };

  const handleSaveWorkspace = () => {
    // Check agency workspace limits before creation
    if (modalType === 'create' && accessType === 'agency') {
      const agencyInfo = workspaceStats?.['agencyInfo'];
      if (agencyInfo && agencyInfo['remainingSlots'] <= 0) {
        EventBus.publish('error', `You've reached your limit of ${agencyInfo['subWorkspaceLimit']} sub-workspaces. Please upgrade your plan to add more.`);
        return;
      }
    }

    // Validate required fields
    if (!formData['name'] || formData['name'].trim() === '') {
      EventBus.publish('error', `${accessType === 'agency' ? 'Sub-workspace' : 'Workspace'} name is required`);
      return;
    }

    if (modalType === 'create' && (!formData['userEmail'] || formData['userEmail'].trim() === '')) {
      EventBus.publish('error', 'Client email is required');
      return;
    }

    if (modalType === 'create' && (!formData['firstName'] || formData['firstName'].trim() === '')) {
      EventBus.publish('error', 'First name is required');
      return;
    }

    // For agency sub-workspaces, validate plan selection
    if (modalType === 'create' && accessType === 'agency' && !formData['plan']) {
      EventBus.publish('error', 'Please select a plan for the sub-workspace');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData['userEmail'] && !emailRegex.test(formData['userEmail'])) {
      EventBus.publish('error', 'Please enter a valid email address');
      return;
    }

    const isEdit = modalType === 'edit' && selectedWorkspace && selectedWorkspace['_id'];
    const action = isEdit ? 'UPDATE_ADMIN_WORKSPACE' : 'CREATE_ADMIN_WORKSPACE';
    const updates = { name: formData['name'].trim(), status: formData['status'] };
    if (formData['description'] !== undefined) updates['description'] = formData['description'].trim();
    if (formData['plan']) updates['planId'] = formData['plan'];  // Send as planId for backend
    if (formData['userEmail']) updates['userEmail'] = formData['userEmail'].trim();
    if (formData['firstName']) updates['firstName'] = formData['firstName'].trim();
    if (formData['lastName']) updates['lastName'] = formData['lastName'].trim();
    if (formData['hideBilling'] !== undefined) {
      updates['hideBilling'] = formData['hideBilling'];
      // Include period dates when toggle is enabled
      if (formData['hideBilling']) {
        if (formData['currentPeriodStart']) updates['currentPeriodStart'] = formData['currentPeriodStart'];
        if (formData['currentPeriodEnd']) updates['currentPeriodEnd'] = formData['currentPeriodEnd'];
      }
    }

    const payload = isEdit 
      ? { id: selectedWorkspace['_id'], updates, filters: { page: currentPage, search: searchTerm, status: statusFilter, plan: planFilter } } 
      : { data: { ...updates }, filters: { page: currentPage, search: searchTerm, status: statusFilter, plan: planFilter } 
    }

    dispatch({ type: action, payload });
    setShowModal(false);
  }

  const getStatusBadge = (status) => {
    const statusColors = { active: 'success', suspended: 'warning', inactive: 'secondary', trial: 'info', deactivated: 'danger' };
    const statusLabels = { active: 'Active', suspended: 'Paused', inactive: 'Inactive', trial: 'Trial', deactivated: 'Deactivated' };
    return <Badge color={statusColors[status] || 'secondary'}>{statusLabels[status] || status}</Badge>;
  }

  const getPlanBadge = (plan) => {
    // Handle different plan name formats
    let displayPlan = plan;
    if (!plan || plan === '-') {
      displayPlan = 'Free';
    } else {
      // Parse technical plan names like "Enterprise_business_year_agency_68b0320f1cc00125188a9e73"
      // Extract the first part (plan name) before underscores
      const parts = plan.split('_');
      if (parts.length > 0) {
        // Capitalize the first part (plan name)
        displayPlan = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);

        // Add plan type if present (business, etc)
        if (parts.length >= 2 && parts[1]) {
          const planType = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
          displayPlan += ` (${planType})`;
        }

        // Add billing interval if present
        if (parts.length >= 3) {
          const interval = parts[2]; // month, year, etc
          if (interval === 'month') {
            displayPlan += ' - Monthly';
          } else if (interval === 'year') {
            displayPlan += ' - Yearly';
          }
        }
      }
    }

    const planColors = {
      Free: 'secondary',
      Starter: 'warning',
      Professional: 'info',
      Enterprise: 'success',
      Starter_business_month: 'warning'
    };

    // Determine color based on the base plan name (first word of displayPlan)
    const basePlanName = displayPlan.split(' ')[0];
    const color = planColors[basePlanName] || planColors[plan] || 'secondary';

    return <Badge pill color={color}>{displayPlan}</Badge>;
  }

  const calculateUsagePercentage = (used, limit) => {
    if (!limit || limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  }

  // Chart data
  const workspaceGrowthData = {
    labels: workspaceStats?.['growth']?.['map'](d => new Date(d['date'])['toLocaleDateString']()) || [],
    datasets: [{ label: 'New Workspaces', data: workspaceStats?.['growth']?.['map'](d => d['count']) || [], borderColor: '#2E2C9ACC', backgroundColor: '#2E2C9ACC', fill: true }]
  }

  const options = {
    responsive: true,
    plugins: { legend: { labels: { color: '#333', font: { size: 12 } } } },
    elements: { line: { tension: 0.4, borderWidth: 2 }, point: { radius: 0 } },
    scales: { x: { grid: { display: false }, ticks: { color: '#666' } }, y: { grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#666' } } }
  }

  // Create plan distribution for pie chart
  const planLabels = ['free', 'trial', 'paid', 'enterprise'];
  const planValues = [workspaceStats?.['summary']?.['free'] || 0, workspaceStats?.['summary']?.['trial'] || 0, workspaceStats?.['summary']?.['paid'] || 0, workspaceStats?.['summary']?.['enterprise'] || 0];
  const backgroundColors = ['#7F8BFA', '#F7931A', '#94C8FB', 'var(--primary-color)'];

  const planDistributionData = {
    labels: planLabels,
    datasets: [{ data: planValues, backgroundColor: backgroundColors }]
  };

  const usageStatsData = {
    labels: ['workspaces', 'Agents', 'Calls'],
    datasets: [{ label: '', data: [workspaceStats?.['summary']?.['totalUsers'] || 0, workspaceStats?.['summary']?.['totalAgents'] || 0, workspaceStats?.['summary']?.['totalCalls'] || 0], backgroundColor: ['var(--primary-color)', '#F7931A', '#7882E0'] }]
  };

  if (loading && !adminWorkspaces) return <Container fluid className="mt-4"><div className="text-center">Loading workspaces management...</div></Container>;

  const workspaceItems = [
    { key: "total", boxClass: "info-box", label: "Total Workspaces", icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.66683 15.1667H3.3335C1.72016 15.1667 0.833496 14.28 0.833496 12.6667V7.33333C0.833496 5.72 1.72016 4.83333 3.3335 4.83333H6.66683C6.94016 4.83333 7.16683 5.06 7.16683 5.33333V12.6667C7.16683 13.72 7.6135 14.1667 8.66683 14.1667C8.94016 14.1667 9.16683 14.3933 9.16683 14.6667C9.16683 14.94 8.94016 15.1667 8.66683 15.1667ZM3.3335 5.83333C2.28016 5.83333 1.8335 6.28 1.8335 7.33333V12.6667C1.8335 13.72 2.28016 14.1667 3.3335 14.1667H6.53349C6.29349 13.7733 6.16683 13.2733 6.16683 12.6667V5.83333H3.3335Z" fill="#4B5563"></path><path d="M6.66683 5.83333H3.3335C3.06016 5.83333 2.8335 5.60667 2.8335 5.33333V4C2.8335 2.98667 3.6535 2.16667 4.66683 2.16667H6.74015C6.89349 2.16667 7.04017 2.23999 7.1335 2.35999C7.22684 2.48665 7.26015 2.64667 7.22015 2.79334C7.18015 2.94 7.16683 3.10667 7.16683 3.33333V5.33333C7.16683 5.60667 6.94016 5.83333 6.66683 5.83333ZM3.8335 4.83333H6.16683V3.33333C6.16683 3.27333 6.16683 3.22 6.16683 3.16667H4.66683C4.20683 3.16667 3.8335 3.54 3.8335 4V4.83333Z" fill="#4B5563"></path><path d="M9.3335 9.16667C9.06016 9.16667 8.8335 8.94 8.8335 8.66667V5.33333C8.8335 5.06 9.06016 4.83333 9.3335 4.83333C9.60683 4.83333 9.8335 5.06 9.8335 5.33333V8.66667C9.8335 8.94 9.60683 9.16667 9.3335 9.16667Z" fill="#4B5563"></path><path d="M12 9.16667C11.7267 9.16667 11.5 8.94 11.5 8.66667V5.33333C11.5 5.06 11.7267 4.83333 12 4.83333C12.2733 4.83333 12.5 5.06 12.5 5.33333V8.66667C12.5 8.94 12.2733 9.16667 12 9.16667Z" fill="#4B5563"></path><path d="M12.0002 15.1667H9.3335C9.06016 15.1667 8.8335 14.94 8.8335 14.6667V12C8.8335 11.36 9.36016 10.8333 10.0002 10.8333H11.3335C11.9735 10.8333 12.5002 11.36 12.5002 12V14.6667C12.5002 14.94 12.2735 15.1667 12.0002 15.1667ZM9.8335 14.1667H11.5002V12C11.5002 11.9067 11.4268 11.8333 11.3335 11.8333H10.0002C9.90683 11.8333 9.8335 11.9067 9.8335 12V14.1667Z" fill="#4B5563"></path><path d="M4 11.8333C3.72667 11.8333 3.5 11.6067 3.5 11.3333V8.66667C3.5 8.39333 3.72667 8.16667 4 8.16667C4.27333 8.16667 4.5 8.39333 4.5 8.66667V11.3333C4.5 11.6067 4.27333 11.8333 4 11.8333Z" fill="#4B5563"></path><path d="M12.6665 15.1667H8.6665C7.05317 15.1667 6.1665 14.28 6.1665 12.6667V3.33333C6.1665 1.72 7.05317 0.833332 8.6665 0.833332H12.6665C14.2798 0.833332 15.1665 1.72 15.1665 3.33333V12.6667C15.1665 14.28 14.2798 15.1667 12.6665 15.1667ZM8.6665 1.83333C7.61317 1.83333 7.1665 2.28 7.1665 3.33333V12.6667C7.1665 13.72 7.61317 14.1667 8.6665 14.1667H12.6665C13.7198 14.1667 14.1665 13.72 14.1665 12.6667V3.33333C14.1665 2.28 13.7198 1.83333 12.6665 1.83333H8.6665Z" fill="#4B5563"></path></svg> },
    { key: "active", boxClass: "success-box", label: "Active Workspaces", icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.66683 15.1667H3.3335C1.72016 15.1667 0.833496 14.28 0.833496 12.6667V7.33333C0.833496 5.72 1.72016 4.83333 3.3335 4.83333H6.66683C6.94016 4.83333 7.16683 5.06 7.16683 5.33333V12.6667C7.16683 13.72 7.6135 14.1667 8.66683 14.1667C8.94016 14.1667 9.16683 14.3933 9.16683 14.6667C9.16683 14.94 8.94016 15.1667 8.66683 15.1667ZM3.3335 5.83333C2.28016 5.83333 1.8335 6.28 1.8335 7.33333V12.6667C1.8335 13.72 2.28016 14.1667 3.3335 14.1667H6.53349C6.29349 13.7733 6.16683 13.2733 6.16683 12.6667V5.83333H3.3335Z" fill="#4B5563"></path><path d="M6.66683 5.83333H3.3335C3.06016 5.83333 2.8335 5.60667 2.8335 5.33333V4C2.8335 2.98667 3.6535 2.16667 4.66683 2.16667H6.74015C6.89349 2.16667 7.04017 2.23999 7.1335 2.35999C7.22684 2.48665 7.26015 2.64667 7.22015 2.79334C7.18015 2.94 7.16683 3.10667 7.16683 3.33333V5.33333C7.16683 5.60667 6.94016 5.83333 6.66683 5.83333ZM3.8335 4.83333H6.16683V3.33333C6.16683 3.27333 6.16683 3.22 6.16683 3.16667H4.66683C4.20683 3.16667 3.8335 3.54 3.8335 4V4.83333Z" fill="#4B5563"></path><path d="M9.3335 9.16667C9.06016 9.16667 8.8335 8.94 8.8335 8.66667V5.33333C8.8335 5.06 9.06016 4.83333 9.3335 4.83333C9.60683 4.83333 9.8335 5.06 9.8335 5.33333V8.66667C9.8335 8.94 9.60683 9.16667 9.3335 9.16667Z" fill="#4B5563"></path><path d="M12 9.16667C11.7267 9.16667 11.5 8.94 11.5 8.66667V5.33333C11.5 5.06 11.7267 4.83333 12 4.83333C12.2733 4.83333 12.5 5.06 12.5 5.33333V8.66667C12.5 8.94 12.2733 9.16667 12 9.16667Z" fill="#4B5563"></path><path d="M12.0002 15.1667H9.3335C9.06016 15.1667 8.8335 14.94 8.8335 14.6667V12C8.8335 11.36 9.36016 10.8333 10.0002 10.8333H11.3335C11.9735 10.8333 12.5002 11.36 12.5002 12V14.6667C12.5002 14.94 12.2735 15.1667 12.0002 15.1667ZM9.8335 14.1667H11.5002V12C11.5002 11.9067 11.4268 11.8333 11.3335 11.8333H10.0002C9.90683 11.8333 9.8335 11.9067 9.8335 12V14.1667Z" fill="#4B5563"></path><path d="M4 11.8333C3.72667 11.8333 3.5 11.6067 3.5 11.3333V8.66667C3.5 8.39333 3.72667 8.16667 4 8.16667C4.27333 8.16667 4.5 8.39333 4.5 8.66667V11.3333C4.5 11.6067 4.27333 11.8333 4 11.8333Z" fill="#4B5563"></path><path d="M12.6665 15.1667H8.6665C7.05317 15.1667 6.1665 14.28 6.1665 12.6667V3.33333C6.1665 1.72 7.05317 0.833332 8.6665 0.833332H12.6665C14.2798 0.833332 15.1665 1.72 15.1665 3.33333V12.6667C15.1665 14.28 14.2798 15.1667 12.6665 15.1667ZM8.6665 1.83333C7.61317 1.83333 7.1665 2.28 7.1665 3.33333V12.6667C7.1665 13.72 7.61317 14.1667 8.6665 14.1667H12.6665C13.7198 14.1667 14.1665 13.72 14.1665 12.6667V3.33333C14.1665 2.28 13.7198 1.83333 12.6665 1.83333H8.6665Z" fill="#4B5563"></path></svg> },
    { key: "suspended", boxClass: "danger-box", label: "Paused Workspaces", icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.66683 15.1667H3.3335C1.72016 15.1667 0.833496 14.28 0.833496 12.6667V7.33333C0.833496 5.72 1.72016 4.83333 3.3335 4.83333H6.66683C6.94016 4.83333 7.16683 5.06 7.16683 5.33333V12.6667C7.16683 13.72 7.6135 14.1667 8.66683 14.1667C8.94016 14.1667 9.16683 14.3933 9.16683 14.6667C9.16683 14.94 8.94016 15.1667 8.66683 15.1667ZM3.3335 5.83333C2.28016 5.83333 1.8335 6.28 1.8335 7.33333V12.6667C1.8335 13.72 2.28016 14.1667 3.3335 14.1667H6.53349C6.29349 13.7733 6.16683 13.2733 6.16683 12.6667V5.83333H3.3335Z" fill="#4B5563"></path><path d="M6.66683 5.83333H3.3335C3.06016 5.83333 2.8335 5.60667 2.8335 5.33333V4C2.8335 2.98667 3.6535 2.16667 4.66683 2.16667H6.74015C6.89349 2.16667 7.04017 2.23999 7.1335 2.35999C7.22684 2.48665 7.26015 2.64667 7.22015 2.79334C7.18015 2.94 7.16683 3.10667 7.16683 3.33333V5.33333C7.16683 5.60667 6.94016 5.83333 6.66683 5.83333ZM3.8335 4.83333H6.16683V3.33333C6.16683 3.27333 6.16683 3.22 6.16683 3.16667H4.66683C4.20683 3.16667 3.8335 3.54 3.8335 4V4.83333Z" fill="#4B5563"></path><path d="M9.3335 9.16667C9.06016 9.16667 8.8335 8.94 8.8335 8.66667V5.33333C8.8335 5.06 9.06016 4.83333 9.3335 4.83333C9.60683 4.83333 9.8335 5.06 9.8335 5.33333V8.66667C9.8335 8.94 9.60683 9.16667 9.3335 9.16667Z" fill="#4B5563"></path><path d="M12 9.16667C11.7267 9.16667 11.5 8.94 11.5 8.66667V5.33333C11.5 5.06 11.7267 4.83333 12 4.83333C12.2733 4.83333 12.5 5.06 12.5 5.33333V8.66667C12.5 8.94 12.2733 9.16667 12 9.16667Z" fill="#4B5563"></path><path d="M12.0002 15.1667H9.3335C9.06016 15.1667 8.8335 14.94 8.8335 14.6667V12C8.8335 11.36 9.36016 10.8333 10.0002 10.8333H11.3335C11.9735 10.8333 12.5002 11.36 12.5002 12V14.6667C12.5002 14.94 12.2735 15.1667 12.0002 15.1667ZM9.8335 14.1667H11.5002V12C11.5002 11.9067 11.4268 11.8333 11.3335 11.8333H10.0002C9.90683 11.8333 9.8335 11.9067 9.8335 12V14.1667Z" fill="#4B5563"></path><path d="M4 11.8333C3.72667 11.8333 3.5 11.6067 3.5 11.3333V8.66667C3.5 8.39333 3.72667 8.16667 4 8.16667C4.27333 8.16667 4.5 8.39333 4.5 8.66667V11.3333C4.5 11.6067 4.27333 11.8333 4 11.8333Z" fill="#4B5563"></path><path d="M12.6665 15.1667H8.6665C7.05317 15.1667 6.1665 14.28 6.1665 12.6667V3.33333C6.1665 1.72 7.05317 0.833332 8.6665 0.833332H12.6665C14.2798 0.833332 15.1665 1.72 15.1665 3.33333V12.6667C15.1665 14.28 14.2798 15.1667 12.6665 15.1667ZM8.6665 1.83333C7.61317 1.83333 7.1665 2.28 7.1665 3.33333V12.6667C7.1665 13.72 7.61317 14.1667 8.6665 14.1667H12.6665C13.7198 14.1667 14.1665 13.72 14.1665 12.6667V3.33333C14.1665 2.28 13.7198 1.83333 12.6665 1.83333H8.6665Z" fill="#4B5563"></path></svg> },
    { key: "deactivated", boxClass: "primery-box", label: "Deactivated Workspaces", icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.66683 15.1667H3.3335C1.72016 15.1667 0.833496 14.28 0.833496 12.6667V7.33333C0.833496 5.72 1.72016 4.83333 3.3335 4.83333H6.66683C6.94016 4.83333 7.16683 5.06 7.16683 5.33333V12.6667C7.16683 13.72 7.6135 14.1667 8.66683 14.1667C8.94016 14.1667 9.16683 14.3933 9.16683 14.6667C9.16683 14.94 8.94016 15.1667 8.66683 15.1667ZM3.3335 5.83333C2.28016 5.83333 1.8335 6.28 1.8335 7.33333V12.6667C1.8335 13.72 2.28016 14.1667 3.3335 14.1667H6.53349C6.29349 13.7733 6.16683 13.2733 6.16683 12.6667V5.83333H3.3335Z" fill="#4B5563"></path><path d="M6.66683 5.83333H3.3335C3.06016 5.83333 2.8335 5.60667 2.8335 5.33333V4C2.8335 2.98667 3.6535 2.16667 4.66683 2.16667H6.74015C6.89349 2.16667 7.04017 2.23999 7.1335 2.35999C7.22684 2.48665 7.26015 2.64667 7.22015 2.79334C7.18015 2.94 7.16683 3.10667 7.16683 3.33333V5.33333C7.16683 5.60667 6.94016 5.83333 6.66683 5.83333ZM3.8335 4.83333H6.16683V3.33333C6.16683 3.27333 6.16683 3.22 6.16683 3.16667H4.66683C4.20683 3.16667 3.8335 3.54 3.8335 4V4.83333Z" fill="#4B5563"></path><path d="M9.3335 9.16667C9.06016 9.16667 8.8335 8.94 8.8335 8.66667V5.33333C8.8335 5.06 9.06016 4.83333 9.3335 4.83333C9.60683 4.83333 9.8335 5.06 9.8335 5.33333V8.66667C9.8335 8.94 9.60683 9.16667 9.3335 9.16667Z" fill="#4B5563"></path><path d="M12 9.16667C11.7267 9.16667 11.5 8.94 11.5 8.66667V5.33333C11.5 5.06 11.7267 4.83333 12 4.83333C12.2733 4.83333 12.5 5.06 12.5 5.33333V8.66667C12.5 8.94 12.2733 9.16667 12 9.16667Z" fill="#4B5563"></path><path d="M12.0002 15.1667H9.3335C9.06016 15.1667 8.8335 14.94 8.8335 14.6667V12C8.8335 11.36 9.36016 10.8333 10.0002 10.8333H11.3335C11.9735 10.8333 12.5002 11.36 12.5002 12V14.6667C12.5002 14.94 12.2735 15.1667 12.0002 15.1667ZM9.8335 14.1667H11.5002V12C11.5002 11.9067 11.4268 11.8333 11.3335 11.8333H10.0002C9.90683 11.8333 9.8335 11.9067 9.8335 12V14.1667Z" fill="#4B5563"></path><path d="M4 11.8333C3.72667 11.8333 3.5 11.6067 3.5 11.3333V8.66667C3.5 8.39333 3.72667 8.16667 4 8.16667C4.27333 8.16667 4.5 8.39333 4.5 8.66667V11.3333C4.5 11.6067 4.27333 11.8333 4 11.8333Z" fill="#4B5563"></path><path d="M12.6665 15.1667H8.6665C7.05317 15.1667 6.1665 14.28 6.1665 12.6667V3.33333C6.1665 1.72 7.05317 0.833332 8.6665 0.833332H12.6665C14.2798 0.833332 15.1665 1.72 15.1665 3.33333V12.6667C15.1665 14.28 14.2798 15.1667 12.6665 15.1667ZM8.6665 1.83333C7.61317 1.83333 7.1665 2.28 7.1665 3.33333V12.6667C7.1665 13.72 7.61317 14.1667 8.6665 14.1667H12.6665C13.7198 14.1667 14.1665 13.72 14.1665 12.6667V3.33333C14.1665 2.28 13.7198 1.83333 12.6665 1.83333H8.6665Z" fill="#4B5563"></path></svg> },
  ];

  return (
    <>
      <div className='content-dashboard'>
        <h3>{accessType === 'agency' ? 'Sub-Workspaces Management' : 'Workspaces Management'}</h3>
        <p className="text-muted mb-3">{accessType === 'agency' ? 'Manage all your sub-workspaces' : 'Manage all workspaces across the platform'}</p>
        <div className='tabs-area'>
          {/* Navigation Tabs */}
          <div className='tab-header'>
            <Nav tabs>
              {['workspaces', 'members', 'analytics', 'settings', ...(accessType !== 'agency' ? ['feedback'] : [])]?.['map']((item) => (
                <NavItem key={item}>
                  <NavLink className={`text-capitalize ${activeTab === item ? 'active' : ''}`} onClick={() => setActiveTab(item)}>
                    {item === 'feedback' ? 'User Feedback' : item}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>
          <TabContent activeTab={activeTab}>

            {/* All Workspaces Tab */}
            <TabPane tabId="workspaces">
              <div className='content-dashboard'>
                <div className='title-area style-workshop'>
                  <div>
                    <h4>{accessType === 'agency' ? 'Agency Workspaces' : 'All Workspaces'}</h4>
                    {accessType === 'agency' && workspaceStats?.agencyInfo && (
                      <div>
                        <p className="text-muted small mb-0">
                          Showing parent workspace and {workspaceStats.agencyInfo.subWorkspaceCount || 0} sub-workspace{workspaceStats.agencyInfo.subWorkspaceCount !== 1 ? 's' : ''}
                        </p>
                        
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      className='btn-style-border'
                      onClick={() => openModal('create', {})}
                      disabled={accessType === 'agency' && workspaceStats?.agencyInfo?.remainingSlots === 0}
                    >
                      + Create {accessType === 'agency' ? 'Sub-Workspace' : 'Workspace'}
                    </button>
                  </div>
                </div>
                <div className="table-container filters-table">
                  {/* Search and Filters */}
                  <div className='header-table-filters advanced-table-filters'>
                    <div className='left-area'>
                      <div className='filter-box'>
                        <div className='searchbox'>
                          <TextField
                            fullWidth
                            hiddenLabel
                            size="small"
                            name='search'
                            variant="standard"
                            placeholder="Search by ID or workspace name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <i className='icon'>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                              <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                            </svg>
                          </i>
                        </div>
                      </div>
                      <div className='filter-box'>
                        <Input
                          type="select"
                          id="status-filter"
                          name="statusFilter"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="">All Status</option>
                          <option value="active">Active</option>
                          <option value="suspended">Paused</option>
                          <option value="deactivated">Deactivated</option>
                          <option value="inactive">Inactive</option>
                          <option value="trial">Trial</option>
                        </Input>
                      </div>
                      <div className='filter-box'>
                        <Input
                          type="select"
                          id="plan-filter"
                          name="planFilter"
                          value={planFilter}
                          onChange={(e) => setPlanFilter(e.target.value)}
                          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                          <option value="">All Plans</option>
                          {Array.isArray(workspacePlans) && workspacePlans.map(plan => (
                            <option key={plan['_id']} value={plan['name']}>
                              {plan['displayText'] || plan['displayName']}
                            </option>
                          ))}
                        </Input>
                      </div>
                    </div>
                    <div className='right-deals-filtes'>
                      <UncontrolledDropdown>
                        <DropdownToggle
                          size="sm"
                          color="secondary"
                          caret
                          disabled={exportLoading}
                          className="mr-2"
                        >
                          <i className="fa fa-download mr-1"></i>
                          {exportLoading ? 'Exporting...' : `Export All ${accessType === 'agency' ? 'Sub-Workspaces' : 'Workspaces'}`}
                        </DropdownToggle>
                        <DropdownMenu right>
                          <DropdownItem onClick={() => handleExportAllWorkspaces('csv')}>
                            <i className="fa fa-file-text-o mr-2"></i>Export as CSV
                          </DropdownItem>
                          <DropdownItem onClick={() => handleExportAllWorkspaces('excel')}>
                            <i className="fa fa-file-excel-o mr-2"></i>Export as Excel
                          </DropdownItem>
                          <DropdownItem onClick={() => handleExportAllWorkspaces('pdf')}>
                            <i className="fa fa-file-pdf-o mr-2"></i>Export as PDF
                          </DropdownItem>
                          <DropdownItem divider />
                          <DropdownItem header>
                            <small className="text-muted">
                              Exports all {workspaceStats?.summary?.total || '0'} workspaces
                              {searchTerm && ` matching "${searchTerm}"`}
                              {statusFilter && ` (Status: ${statusFilter})`}
                            </small>
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </div>
                  </div>
                  <div className='table-area'>
                    <BulkActions
                      selectedItems={selectedWorkspaces}
                      totalItems={(adminWorkspaces || []).length}
                      onSelectAll={handleSelectAll}
                      onDeselectAll={handleDeselectAll}
                      actions={workspaceBulkActions}
                      onAction={handleBulkAction}
                      isLoading={loading}
                    />
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th width="30"><Checkbox checked={selectAll} onChange={handleSelectAll} /></th>
                          {['ID', 'Workspace', 'Owner', 'Plan', 'Status', 'Usage', 'Created', 'Actions']?.['map']((item) => (
                            <th>{item}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(adminWorkspaces || []).map(workspace => (
                          <tr key={workspace['_id']}>
                            <td><Checkbox checked={selectedWorkspaces.includes(workspace['_id'])} onChange={() => handleWorkspaceSelect(workspace['_id'])} /></td>
                            <td>
                              <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                <span
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    navigator.clipboard.writeText(workspace['_id']);
                                    EventBus.publish('success', 'ID copied to clipboard');
                                  }}
                                  title="Click to copy ID"
                                >
                                  {workspace['_id']}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{workspace['name']}</strong>
                                {workspace['workspaceType'] === 'parent' && (
                                  <Badge color="primary" className="ml-2">Parent</Badge>
                                )}
                                {workspace['workspaceType'] === 'child' && (
                                  <Badge color="secondary" className="ml-2">Sub-Workspace</Badge>
                                )}
                                <br />
                                <small className="text-muted">{workspace['description']}</small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{workspace['createdBy']?.['firstName']} {workspace['createdBy']?.['lastName']}</strong>
                                <br />
                                <small className="text-muted">{workspace['createdBy']?.['email']}</small>
                              </div>
                            </td>
                            <td>{getPlanBadge(workspace['currentSubscription']?.['planName'])}</td>
                            <td>{getStatusBadge(workspace?.['computedStatus'] || workspace?.['status'] || 'inactive')}</td>
                            <td>
                              <div className="mb-1">
                                <small>Users: {workspace['memberCount'] || 0}/{workspace['limits']?.['maxUsers'] || '∞'}</small>
                              </div>
                              <div>
                                <small>Agents: {workspace['agentCount'] || 0}/{workspace['limits']?.['maxAgents'] || '∞'}</small>
                              </div>
                            </td>
                            <td>{new Date(workspace['createdAt'])['toLocaleDateString']()}</td>
                            <td>
                              <div className='action-box'>
                                <button className='action-btn' color="secondary" onClick={() => openModal('view', workspace)} id={`view-${workspace['_id']}`}>
                                  <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#323B43" /><path d="M12 14.165C10.805 14.165 9.83496 13.195 9.83496 12C9.83496 10.805 10.805 9.83501 12 9.83501C13.195 9.83501 14.165 10.805 14.165 12C14.165 13.195 13.195 14.165 12 14.165ZM12 10.585C11.22 10.585 10.585 11.22 10.585 12C10.585 12.78 11.22 13.415 12 13.415C12.78 13.415 13.415 12.78 13.415 12C13.415 11.22 12.78 10.585 12 10.585Z" fill="#323B43" /><path d="M12 16.51C10.12 16.51 8.34504 15.41 7.12504 13.5C6.59504 12.675 6.59504 11.33 7.12504 10.5C8.35004 8.58999 10.125 7.48999 12 7.48999C13.875 7.48999 15.65 8.58999 16.87 10.5C17.4 11.325 17.4 12.67 16.87 13.5C15.65 15.41 13.875 16.51 12 16.51ZM12 8.23999C10.385 8.23999 8.84004 9.20999 7.76004 10.905C7.38504 11.49 7.38504 12.51 7.76004 13.095C8.84004 14.79 10.385 15.76 12 15.76C13.615 15.76 15.16 14.79 16.24 13.095C16.615 12.51 16.615 11.49 16.24 10.905C15.16 9.20999 13.615 8.23999 12 8.23999Z" fill="#323B43" /></svg></i>
                                  <UncontrolledTooltip target={`view-${workspace['_id']}`}>
                                    View Details
                                  </UncontrolledTooltip>
                                </button>
                                <button className='action-btn' color="primary" onClick={() => openModal('edit', workspace)} id={`edit-${workspace['_id']}`}>
                                  <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#A46497" /><path d="M13.5 17.375H10.5C7.785 17.375 6.625 16.215 6.625 13.5V10.5C6.625 7.785 7.785 6.625 10.5 6.625H11.5C11.705 6.625 11.875 6.795 11.875 7C11.875 7.205 11.705 7.375 11.5 7.375H10.5C8.195 7.375 7.375 8.195 7.375 10.5V13.5C7.375 15.805 8.195 16.625 10.5 16.625H13.5C15.805 16.625 16.625 15.805 16.625 13.5V12.5C16.625 12.295 16.795 12.125 17 12.125C17.205 12.125 17.375 12.295 17.375 12.5V13.5C17.375 16.215 16.215 17.375 13.5 17.375Z" fill="#A46497" /><path d="M10.2497 14.845C9.94467 14.845 9.66467 14.735 9.45967 14.535C9.21467 14.29 9.10967 13.935 9.16467 13.56L9.37967 12.055C9.41967 11.765 9.60967 11.39 9.81467 11.185L13.7547 7.245C14.7497 6.25 15.7597 6.25 16.7547 7.245C17.2997 7.79 17.5447 8.345 17.4947 8.9C17.4497 9.35 17.2097 9.79 16.7547 10.24L12.8147 14.18C12.6097 14.385 12.2347 14.575 11.9447 14.615L10.4397 14.83C10.3747 14.845 10.3097 14.845 10.2497 14.845ZM14.2847 7.775L10.3447 11.715C10.2497 11.81 10.1397 12.03 10.1197 12.16L9.90467 13.665C9.88467 13.81 9.91467 13.93 9.98967 14.005C10.0647 14.08 10.1847 14.11 10.3297 14.09L11.8347 13.875C11.9647 13.855 12.1897 13.745 12.2797 13.65L16.2197 9.71C16.5447 9.385 16.7147 9.095 16.7397 8.825C16.7697 8.5 16.5997 8.155 16.2197 7.77C15.4197 6.97 14.8697 7.195 14.2847 7.775Z" fill="#A46497" /><path d="M15.9248 10.915C15.8898 10.915 15.8548 10.91 15.8248 10.9C14.5098 10.53 13.4648 9.485 13.0948 8.17C13.0398 7.97 13.1548 7.765 13.3548 7.705C13.5548 7.65 13.7598 7.765 13.8148 7.965C14.1148 9.03 14.9598 9.875 16.0248 10.175C16.2248 10.23 16.3398 10.44 16.2848 10.64C16.2398 10.81 16.0898 10.915 15.9248 10.915Z" fill="#A46497" /></svg></i>
                                  <UncontrolledTooltip target={`edit-${workspace['_id']}`}>
                                    Edit Workspace
                                  </UncontrolledTooltip>
                                </button>

                                <button
                                  title="Impersonate Workspace Owner"
                                  id={`impersonate-${workspace['_id']}`}
                                  className='action-btn impersonate-btn'
                                  onClick={() => handleImpersonateWorkspace(workspace)}
                                >
                                  <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#C73A63" /><path d="M14.4809 11.8077C14.9828 11.9323 15.467 12.121 15.9119 12.3951C16.1184 12.5214 16.3142 12.6656 16.51 12.8098C16.6577 12.9201 16.6666 13.0465 16.5474 13.1889C16.3587 13.4149 16.1665 13.6392 15.9742 13.8635C15.9689 13.8706 15.9671 13.8795 15.9582 13.8937C16.3658 14.1394 16.777 14.3797 17.1792 14.6324C17.3216 14.7214 17.4587 14.8246 17.5744 14.9457C17.8752 15.2572 18.0033 15.6416 18.0033 16.0706C18.0033 16.6081 18.0051 17.1456 18.0033 17.685C18.0033 17.9163 17.9215 18 17.6901 18C13.8989 18 10.1076 18 6.31465 18C6.08326 18 5.99961 17.9181 6.00139 17.685C6.00317 17.0869 5.99071 16.4871 6.0174 15.8908C6.04232 15.3568 6.31643 14.9492 6.76853 14.668C7.15477 14.4277 7.54813 14.1981 7.93615 13.9632C7.97353 13.9418 8.00913 13.9187 8.05362 13.8902C7.85783 13.6588 7.66738 13.4345 7.47693 13.2103C7.33988 13.0501 7.34522 12.9486 7.50719 12.8151C8.06964 12.3488 8.70507 12.0248 9.4117 11.8451C9.45263 11.8344 9.49535 11.8219 9.52917 11.813C9.46153 11.5407 9.39746 11.2737 9.32448 11.0085C9.31558 10.9747 9.26218 10.9444 9.22481 10.9302C8.81543 10.7896 8.43274 10.5973 8.12126 10.2894C7.72078 9.89606 7.70832 9.46888 8.08566 9.0506C8.37401 8.732 8.74245 8.52909 9.13937 8.38136C9.23371 8.34576 9.28532 8.29414 9.31914 8.20159C9.49891 7.71033 9.68046 7.21907 9.87269 6.73316C9.93143 6.58187 10.0097 6.43235 10.1023 6.29886C10.2874 6.03187 10.5277 5.94466 10.841 6.03365C11.019 6.08349 11.1898 6.16181 11.3589 6.23478C11.4817 6.28818 11.5992 6.35404 11.7185 6.41455C11.9107 6.51245 12.1011 6.50711 12.2934 6.41455C12.5034 6.31132 12.7117 6.20452 12.9235 6.10841C13.0178 6.06569 13.1193 6.03721 13.2207 6.01941C13.5002 5.96958 13.7333 6.06035 13.8828 6.30242C13.9932 6.48219 14.084 6.6762 14.1587 6.87199C14.326 7.30807 14.4809 7.74949 14.6357 8.18913C14.666 8.27634 14.7105 8.32796 14.8048 8.36178C15.1662 8.48993 15.5043 8.66614 15.7927 8.92245C15.8692 8.99187 15.9422 9.06662 16.0063 9.1485C16.2715 9.49024 16.2626 9.86224 15.9831 10.1915C15.6752 10.5546 15.264 10.7558 14.8244 10.9035C14.7123 10.9409 14.6749 10.9978 14.6535 11.1082C14.6055 11.3413 14.5396 11.5692 14.4809 11.8077ZM7.98065 13.0643C8.24051 13.3598 8.4897 13.6463 8.74245 13.9311C9.2426 14.4954 9.75166 15.0578 10.0667 15.7538C10.3106 16.2931 10.5241 16.8466 10.7449 17.3966C10.7822 17.4874 10.8214 17.5194 10.9193 17.5194C11.6384 17.5141 12.3592 17.5141 13.0783 17.5194C13.1851 17.5194 13.2225 17.4838 13.2599 17.3895C13.4735 16.8466 13.6835 16.302 13.9202 15.768C14.2281 15.072 14.739 14.5167 15.2356 13.956C15.4954 13.6624 15.7553 13.3669 16.0205 13.0643C15.5026 12.6674 14.9383 12.4075 14.3136 12.2847C14.2798 12.2776 14.2228 12.3167 14.1961 12.3506C14.0377 12.5392 13.8882 12.7368 13.7262 12.9201C13.6675 12.986 13.6336 13.0447 13.6354 13.1337C13.6408 13.4114 13.639 13.6891 13.6372 13.9667C13.6354 14.1323 13.5411 14.2355 13.4023 14.2373C13.2563 14.239 13.1602 14.134 13.1531 13.9649C13.1513 13.9169 13.1531 13.8688 13.1531 13.8225C13.1531 13.6766 13.1531 13.5324 13.1531 13.3687C12.7793 13.5627 12.4055 13.6641 12.0032 13.6641C11.5974 13.6624 11.2236 13.5502 10.8534 13.358C10.8534 13.5716 10.8534 13.762 10.8534 13.9525C10.8516 14.1394 10.7537 14.2586 10.606 14.2568C10.4618 14.2533 10.3675 14.1376 10.3675 13.9578C10.3657 13.6713 10.3711 13.3847 10.3639 13.0999C10.3622 13.0465 10.3337 12.986 10.3017 12.9433C10.145 12.7421 9.97771 12.5499 9.82464 12.3452C9.77836 12.2847 9.7392 12.2722 9.67334 12.2865C9.05749 12.42 8.49682 12.6727 7.98065 13.0643ZM11.8448 9.37633C11.6419 9.40481 11.2806 9.44218 10.9228 9.50982C10.6042 9.57034 10.2945 9.67535 10.0311 9.88004C9.84955 10.0207 9.75344 10.2004 9.75522 10.4389C9.76412 11.142 9.97593 11.7756 10.4013 12.3345C11.213 13.3989 12.5764 13.4666 13.4806 12.4841C13.8633 12.0693 14.0804 11.5674 14.1854 11.0174C14.2103 10.8857 14.2264 10.7522 14.2353 10.6187C14.2655 10.2058 14.2157 9.95658 13.6621 9.70027C13.1406 9.45286 12.5764 9.39591 11.8448 9.37633ZM6.50332 17.5105C7.75282 17.5105 9.00944 17.5105 10.2589 17.5105C9.91007 16.2681 9.31202 15.1735 8.36511 14.2746C7.90233 14.5505 7.43065 14.8246 6.96788 15.113C6.70979 15.275 6.52646 15.5206 6.51044 15.8232C6.48552 16.3803 6.50332 16.9392 6.50332 17.5105ZM17.521 17.5123C17.521 17.4642 17.521 17.4251 17.521 17.3877C17.521 16.941 17.5245 16.4942 17.5192 16.0492C17.5156 15.6274 17.3323 15.2999 16.9763 15.0738C16.8339 14.983 16.6862 14.8994 16.542 14.814C16.2377 14.6342 15.9333 14.4526 15.6378 14.28C14.6909 15.1806 14.0911 16.2717 13.744 17.5141C14.9953 17.5123 16.2483 17.5123 17.521 17.5123ZM14.7372 10.4283C15.0433 10.3215 15.3174 10.1773 15.5399 9.94946C15.7535 9.73231 15.7535 9.49202 15.5204 9.29445C15.3602 9.15918 15.1768 9.04348 14.9864 8.95449C14.2976 8.63232 13.5589 8.51663 12.8095 8.45789C12.0424 8.39738 11.277 8.41874 10.5152 8.53443C9.94033 8.62165 9.37432 8.7498 8.85992 9.03458C8.69617 9.12536 8.54666 9.24995 8.41317 9.38167C8.28857 9.50626 8.27433 9.67357 8.37223 9.82487C8.58226 10.1452 8.9151 10.2894 9.26396 10.4318C9.27464 10.3677 9.28354 10.325 9.28888 10.2841C9.32092 9.95836 9.47221 9.68959 9.73564 9.50982C9.95457 9.36031 10.1949 9.22503 10.444 9.14138C11.2824 8.85837 12.1421 8.83701 13.0089 8.99543C13.445 9.07552 13.8668 9.2019 14.2317 9.47066C14.4613 9.63975 14.6304 9.85512 14.6945 10.1381C14.7158 10.2289 14.723 10.3232 14.7372 10.4283ZM14.1142 8.16421C13.9291 7.66227 13.7689 7.19772 13.5838 6.74206C13.4806 6.48753 13.3239 6.44481 13.0676 6.56051C12.8629 6.65306 12.6636 6.75808 12.4607 6.85241C12.1563 6.99125 11.8484 6.99125 11.544 6.85241C11.3874 6.78122 11.2308 6.70468 11.0777 6.62636C10.6398 6.40387 10.4885 6.45905 10.3106 6.91649C10.1646 7.29561 10.0258 7.67651 9.88337 8.05741C9.87447 8.08233 9.87091 8.11081 9.86201 8.15709C11.2753 7.86162 12.6761 7.8634 14.1142 8.16421Z" fill="#C73A63" stroke="#C73A63" stroke-width="0.184615" /></svg></i>
                                  <UncontrolledTooltip target={`impersonate-${workspace['_id']}`}>
                                    Impersonate Owner
                                  </UncontrolledTooltip>
                                </button>

                                {(workspace?.['computedStatus'] || workspace?.['status']) !== 'deactivated' && (
                                  <>
                                    <span id={`ws-toggle-${workspace['_id']}`}>
                                      {workspace['status'] === 'active' ? (
                                        <button className='action-btn'
                                          size="sm"
                                          color="warning"
                                          onClick={() => handleWorkspaceAction(workspace['_id'], 'suspend')}
                                        >
                                          <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#FF8B00"></circle><path d="M10.32 16.875H8.505C7.575 16.875 7.125 16.445 7.125 15.555V8.445C7.125 7.555 7.575 7.125 8.505 7.125H10.32C11.25 7.125 11.7 7.555 11.7 8.445V15.555C11.7 16.445 11.25 16.875 10.32 16.875ZM8.505 7.875C7.965 7.875 7.875 8.01 7.875 8.445V15.555C7.875 15.99 7.96 16.125 8.505 16.125H10.32C10.86 16.125 10.95 15.99 10.95 15.555V8.445C10.95 8.01 10.865 7.875 10.32 7.875H8.505Z" fill="#FF8B00"></path><path d="M15.4948 16.875H13.6798C12.7498 16.875 12.2998 16.445 12.2998 15.555V8.445C12.2998 7.555 12.7498 7.125 13.6798 7.125H15.4948C16.4248 7.125 16.8748 7.555 16.8748 8.445V15.555C16.8748 16.445 16.4248 16.875 15.4948 16.875ZM13.6798 7.875C13.1398 7.875 13.0498 8.01 13.0498 8.445V15.555C13.0498 15.99 13.1348 16.125 13.6798 16.125H15.4948C16.0348 16.125 16.1248 15.99 16.1248 15.555V8.445C16.1248 8.01 16.0398 7.875 15.4948 7.875H13.6798Z" fill="#FF8B00"></path></svg></i>
                                        </button>
                                      ) : (
                                        <button className='action-btn'
                                          color="success"
                                          onClick={() => handleWorkspaceAction(workspace['_id'], 'activate')}
                                        >
                                          <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#138B3F"></circle><path d="M10.1917 16.1708C9.82417 16.1708 9.48 16.0833 9.17667 15.9083C8.45917 15.4942 8.0625 14.66 8.0625 13.5633V10.4425C8.0625 9.34584 8.45917 8.51167 9.17667 8.09751C9.89417 7.68334 10.81 7.75917 11.7667 8.30751L14.4675 9.86501C15.4183 10.4133 15.9433 11.1717 15.9433 12C15.9433 12.8283 15.4183 13.5867 14.4675 14.135L11.7667 15.6925C11.2183 16.0133 10.6817 16.1708 10.1917 16.1708ZM10.1917 8.71001C9.97583 8.71001 9.7775 8.75667 9.60833 8.85584C9.17667 9.10667 8.9375 9.66667 8.9375 10.4425V13.5575C8.9375 14.3275 9.17667 14.8933 9.61417 15.1442C10.0517 15.395 10.6583 15.3192 11.3292 14.9342L14.03 13.3767C14.7008 12.9917 15.0683 12.5017 15.0683 12C15.0683 11.4983 14.7008 11.0083 14.03 10.6233L11.3292 9.06584C10.915 8.82667 10.53 8.71001 10.1917 8.71001Z" fill="#138B3F"></path></svg></i>
                                        </button>
                                      )}
                                    </span>
                                    <UncontrolledTooltip target={`ws-toggle-${workspace['_id']}`}>
                                      {workspace['status'] === 'active' ? 'Pause' : 'Activate'}
                                    </UncontrolledTooltip>
                                    <button
                                      className='action-btn text-danger'
                                      onClick={() => handleWorkspaceAction(workspace['_id'], 'delete')}
                                      title="Deactivate Workspace"
                                    >
                                      <i className="fa fa-trash"></i>
                                    </button>
                                  </>
                                )}
                                {(workspace?.['computedStatus'] || workspace?.['status']) === 'deactivated' && (
                                  <button
                                    className='action-btn text-success'
                                    onClick={() => handleWorkspaceAction(workspace['_id'], 'restore')}
                                    title="Reactivate Workspace"
                                    id={`restore-${workspace['_id']}`}
                                  >
                                    <i className="fa fa-undo"></i>
                                    <UncontrolledTooltip target={`restore-${workspace['_id']}`}>
                                      Reactivate Workspace
                                    </UncontrolledTooltip>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {/* Pagination */}
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination>
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink
                            previous
                            onClick={() => setCurrentPage(currentPage - 1)}
                          />
                        </PaginationItem>

                        {[...Array(Math.min(5, workspacesPagination?.pages || 1))].map((_, i) => {
                          const page = i + 1;
                          return (
                            <PaginationItem key={page} active={page === currentPage}>
                              <PaginationLink onClick={() => setCurrentPage(page)}>
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        })}

                        <PaginationItem disabled={currentPage === (workspacesPagination?.pages || 1)}>
                          <PaginationLink
                            next
                            onClick={() => setCurrentPage(currentPage + 1)}
                          />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  </div>
                </div>
              </div>
            </TabPane>

            {/* Members Tab */}
            <TabPane tabId="members">
              <UsersManagement
                accessType={accessType}
                workspaceId={workspaceId}
                isTabMode={true}
              />
            </TabPane>

            {/* Analytics Tab */}
            <TabPane tabId="analytics">
              {/* Enhanced Agency Info Card for Agency Owners */}
              {accessType === 'agency' && workspaceStats?.['agencyInfo'] && (
                <Card className="mb-3 agency-info-card" style={{ borderLeft: '4px solid #2E2C9ACC' }}>
                  <CardBody>
                    <Row>
                      <Col md={6}>
                        <div className="">
                          <h4 className="mb-0">
                            <i className="fa fa-building mr-2"></i>
                            Your Agency Plan: <b>{workspaceStats['agencyInfo']['planName']}</b>
                          </h4>
                          <div className="mb-2">
                            <small className="text-muted">Plan Type:</small> <Badge color="primary">{workspaceStats['agencyInfo']['planType']}</Badge>
                          </div>
                          {workspaceStats['agencyInfo']['remainingSlots'] === 0 && <Badge color="warning">Limit Reached - Upgrade to add more workspaces</Badge>}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-right">
                          <button
                            size="sm"
                            className="btn-style-new mr-2"
                            onClick={() => openModal('create', {})}
                            disabled={workspaceStats['agencyInfo']['remainingSlots'] === 0}
                          >
                            + Create Sub-Workspace
                          </button>
                          {workspaceStats['agencyInfo']['subWorkspaceCount'] === 0 && (
                            <button
                              size="sm"
                              color="warning"
                              className="btn-style-border"
                              onClick={handleFixChildWorkspaces}
                              title="Fix orphaned workspaces to appear as sub-workspaces"
                            >
                              <i className="fa fa-wrench mr-1"></i> Fix Workspaces
                            </button>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row>


                      <Col md={12}>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Sub-Workspaces Used</span>
                          <strong>{workspaceStats['agencyInfo']['subWorkspaceCount']} / {workspaceStats['agencyInfo']['subWorkspaceLimit']}</strong>
                        </div>
                        <Progress
                          className="mb-2"
                          style={{ height: '10px' }}
                          value={(workspaceStats['agencyInfo']['subWorkspaceCount'] / workspaceStats['agencyInfo']['subWorkspaceLimit']) * 100}
                          color={workspaceStats['agencyInfo']['remainingSlots'] > 5 ? "success" : workspaceStats['agencyInfo']['remainingSlots'] > 0 ? "warning" : "danger"}
                        />
                        <small className={workspaceStats['agencyInfo']['remainingSlots'] > 0 ? "text-success" : "text-danger"}>
                          {workspaceStats['agencyInfo']['remainingSlots'] > 0
                            ? `${workspaceStats['agencyInfo']['remainingSlots']} slots remaining`
                            : 'No slots available - Upgrade your plan to add more workspaces'
                          }
                        </small>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}
              {/* Workspace Summary */}
              <div className='counter-sections mt-3'>
                {workspaceItems.map((item) => (
                  <div key={item.key} className={`counter-box ${item.boxClass}`}>
                    <div className='counter-start'>
                      <div className='left-text-area'>
                        <h5>{item.label}</h5>
                        <div className='CountUp-box'>
                          <h3>{workspaceStats?.summary?.[item.key] || 0}</h3>
                        </div>
                      </div>
                      <div className='right-icon'> <span className='icon'>{item.icon}</span> </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Workspace Growth (Last 30 Days) & Plan Distribution */}
              <div className='chart-box'>
                <div className='row'>
                  <div className='col-lg-8 col-md-12'>
                    <div className='chart-area'>
                      <h4>Workspace Growth (Last 30 Days)</h4>
                      <div style={{ height: '300px' }}>
                        <Line
                          data={workspaceGrowthData}
                          type='area'
                          options={{
                            type: 'area',
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: { x: { display: false }, y: { beginAtZero: true, stepSize: 1 } }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-4 col-md-12'>
                    <div className='chart-area'>
                      <h4>Plan Distribution</h4>
                      <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Doughnut
                          data={planDistributionData || { 'labels': [], 'datasets': [] }}
                          options={{ 'responsive': true, 'maintainAspectRatio': false }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6 col-md-12'>
                    <div className='chart-area'>
                      <div className='table-area table-workspaces'>
                        <h4>Recent Workspaces</h4>
                        <Table size="sm">
                          <thead><tr>{['Name', 'Plan', 'Users', 'Status']?.['map']((item) => (<th>{item}</th>))}</tr></thead>
                          <tbody>
                            {(workspaceStats?.['recentWorkspaces'] || []).map(workspace => (
                              <tr key={workspace['_id']}>
                                <td><strong>{workspace['name']}</strong><br /><small className="text-muted">{workspace?.['createdBy']?.['firstName'] || ''} {workspace?.['createdBy']?.['lastName'] || ''}</small></td>
                                <td>{workspace?.['currentSubscription']?.['planName'] ? getPlanBadge(workspace['currentSubscription']?.['planName']) : '-'}</td>
                                <td>{workspace['memberCount'] || 0}</td>
                                <td><p className={`text-${workspace?.['currentSubscription'] ? 'success' : 'warning'}`}><strong>{workspace?.['currentSubscription'] ? 'Active' : 'Paused'}</strong></p></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6 col-md-12'>
                    {/* Recent Workspaces */}
                    <div className='chart-area'>
                      <h4>Usage Statistics</h4>
                      <div style={{ height: '365px' }}>
                        <Bar
                          data={usageStatsData}
                          options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: false }, scales: { x: { display: false }, y: { beginAtZero: true } } }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='content-dashboard'>
                <div className='row'>
                  <div className='col-lg-6 col-md-12'>
                    <div className='chart-area'>
                      <div className='table-area table-workspaces'>
                        <h4>Workspace Activity</h4>
                        <Table>
                          <thead><tr>{['Metric', 'This Month', 'Last Month', 'Change']?.['map']((item) => (<th>{item}</th>))}</tr></thead>
                          <tbody>
                            <tr>
                              <td>New Workspaces</td>
                              <td>{workspaceStats?.['workspaceActivity']?.['newWorkspaces']?.['thisMonth'] || 0}</td>
                              <td>{workspaceStats?.['workspaceActivity']?.['newWorkspaces']?.['lastMonth'] || 0}</td>
                              <td>
                                <Badge color={workspaceStats?.['workspaceActivity']?.['newWorkspaces']?.['change'] > 0 ? 'success' : 'danger'}>{workspaceStats?.['workspaceActivity']?.['newWorkspaces']?.['change'] || 0}%</Badge>
                              </td>
                            </tr>
                            <tr>
                              <td>Active Users</td>
                              <td>{workspaceStats?.['workspaceActivity']?.['activeUsers']?.['thisMonth'] || 0}</td>
                              <td>{workspaceStats?.['workspaceActivity']?.['activeUsers']?.['lastMonth'] || 0}</td>
                              <td>
                                <Badge color={workspaceStats?.['workspaceActivity']?.['activeUsers']?.['change'] > 0 ? 'success' : 'danger'}>{workspaceStats?.['workspaceActivity']?.['activeUsers']?.['change'] || 0}%</Badge>
                              </td>
                            </tr>
                            <tr>
                              <td>Total Agents</td>
                              <td>{workspaceStats?.['workspaceActivity']?.['totalAgents']?.['thisMonth'] || 0}</td>
                              <td>{workspaceStats?.['workspaceActivity']?.['totalAgents']?.['lastMonth'] || 0}</td>
                              <td>
                                <Badge color={workspaceStats?.['workspaceActivity']?.['totalAgents']?.['change'] > 0 ? 'success' : 'danger'}>{workspaceStats?.['workspaceActivity']?.['totalAgents']?.['change'] || 0}%</Badge>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6 col-md-12'>
                    <div className='chart-area'>
                      <div className='table-area table-workspaces'>
                        <h4>Top Workspaces by Usage</h4>
                        <Table>
                          <thead><tr>{['Workspace', 'Users', 'Agents', 'Calls']?.['map']((item) => (<th className='text-capitalize'>{item}</th>))}</tr></thead>
                          <tbody>
                            {workspaceStats?.['topWorkspaces']?.['length'] > 0 ? (
                              (workspaceStats?.['topWorkspaces']).map((workspace, index) => (
                                <tr key={workspace['_id']}>
                                  <td><strong>{workspace['name']}</strong><br /><small className="text-muted">#{index + 1}</small></td>
                                  <td>{workspace['memberCount']}</td>
                                  <td>{workspace['agentCount']}</td>
                                  <td>{workspace['totalCalls']}</td>
                                </tr>
                              ))
                            ) : (
                              <tr><td colSpan={4} className='text-center p-4'>No workspaces found</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='chart-area'>
                  <div className='table-area table-workspaces'>
                    <h4>Workspace Performance Metrics</h4>
                    <Alert color="info">
                      <h6>Key Insights</h6>
                      <ul className="mb-0">
                        <li>Professional plan workspaces have 3x higher agent usage</li>
                        <li>Average workspace lifespan: 8.5 months</li>
                        <li>Top conversion rate from trial to paid: 68%</li>
                        <li>Most active day: Tuesday (23% more activity)</li>
                      </ul>
                    </Alert>
                  </div>
                </div>
              </div>
            </TabPane>

            {/* Settings Tab */}
            <TabPane tabId="settings">
              <div className='content-dashboard'>
                <div className='row'>
                  {/* Quick Actions Section */}
                  <div className='col-lg-6 col-md-12'>
                    <div className='chart-area'>
                      <div className='table-area'>
                        <h4>Quick Actions</h4>
                        <div className="mb-4">
                          <button
                            className='btn-style-new w-100 mb-2'
                            onClick={async () => {
                              EventBus.publish('info', 'Fetching all workspaces for export...');
                              try {
                                // Fetch ALL workspaces using pagination
                                let allWorkspaces = [];
                                let page = 1;
                                let hasMore = true;
                                const limit = 100;

                                while (hasMore) {
                                  const response = await new Promise((resolve) => {
                                    dispatch(getAdminWorkspaces({
                                      page,
                                      limit,
                                      search: searchTerm || '',
                                      status: statusFilter || '',
                                      plan: planFilter || '',
                                      callback: (data) => resolve(data)
                                    }));
                                  });

                                  if (response && response.workspaces) {
                                    allWorkspaces = [...allWorkspaces, ...response.workspaces];
                                    hasMore = response.pagination && page < Math.ceil(response.pagination.total / limit);
                                    page++;
                                  } else {
                                    // Fallback: use direct API call
                                    const apiResponse = await fetch(`/api/admin/workspaces?page=${page}&limit=${limit}&search=${searchTerm || ''}&status=${statusFilter || ''}&plan=${planFilter || ''}`, {
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                        'Content-Type': 'application/json'
                                      }
                                    });

                                    if (apiResponse.ok) {
                                      const data = await apiResponse.json();
                                      if (data.body && data.body.workspaces) {
                                        allWorkspaces = [...allWorkspaces, ...data.body.workspaces];
                                        hasMore = data.body.pagination && page < Math.ceil(data.body.pagination.total / limit);
                                        page++;
                                      } else {
                                        hasMore = false;
                                      }
                                    } else {
                                      hasMore = false;
                                    }
                                  }

                                  // Prevent infinite loop
                                  if (page > 100) hasMore = false;
                                }

                                if (allWorkspaces.length === 0) {
                                  EventBus.publish('warning', 'No workspaces found to export');
                                  return;
                                }

                                const csvContent = [
                                  ['ID', 'Workspace Name', 'Owner', 'Email', 'Plan', 'Status', 'Users', 'Agents', 'Created'],
                                  ...allWorkspaces.map(w => [
                                    `"${w._id || ''}"`,
                                    `"${w.name || ''}"`,
                                    `"${w.createdBy?.firstName || ''} ${w.createdBy?.lastName || ''}"`,
                                    `"${w.createdBy?.email || ''}"`,
                                    `"${w.currentSubscription?.planName || 'Free'}"`,
                                    `"${w.status || ''}"`,
                                    w.memberCount || 0,
                                    w.agentCount || 0,
                                    `"${new Date(w.createdAt).toLocaleDateString()}"`
                                  ])
                                ].map(row => row.join(',')).join('\n');

                                const blob = new Blob([csvContent], { type: 'text/csv' });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `workspaces_export_${new Date().toISOString().split('T')[0]}.csv`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                                EventBus.publish('success', `Exported ${allWorkspaces.length} workspaces successfully`);
                              } catch (error) {
                                console.error('Export error:', error);
                                EventBus.publish('error', 'Failed to export workspaces');
                              }
                            }}
                          >
                            <i className="fa fa-download mr-2"></i>
                            Export All {accessType === 'agency' ? 'Sub-Workspaces' : 'Workspaces'} to CSV
                          </button>

                          <button
                            className='btn-style-new w-100 mb-2'
                            onClick={async () => {
                              EventBus.publish('info', 'Generating comprehensive report...');
                              try {
                                // Fetch ALL workspaces using pagination for report
                                let allWorkspaces = [];
                                let page = 1;
                                let hasMore = true;
                                const limit = 100;

                                while (hasMore) {
                                  const response = await new Promise((resolve) => {
                                    dispatch(getAdminWorkspaces({
                                      page,
                                      limit,
                                      search: '',
                                      status: '',
                                      plan: '',
                                      callback: (data) => resolve(data)
                                    }));
                                  });

                                  if (response && response.workspaces) {
                                    allWorkspaces = [...allWorkspaces, ...response.workspaces];
                                    hasMore = response.pagination && page < Math.ceil(response.pagination.total / limit);
                                    page++;
                                  } else {
                                    // Fallback: use direct API call
                                    const apiResponse = await fetch(`/api/admin/workspaces?page=${page}&limit=${limit}`, {
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                        'Content-Type': 'application/json'
                                      }
                                    });

                                    if (apiResponse.ok) {
                                      const data = await apiResponse.json();
                                      if (data.body && data.body.workspaces) {
                                        allWorkspaces = [...allWorkspaces, ...data.body.workspaces];
                                        hasMore = data.body.pagination && page < Math.ceil(data.body.pagination.total / limit);
                                        page++;
                                      } else {
                                        hasMore = false;
                                      }
                                    } else {
                                      hasMore = false;
                                    }
                                  }

                                  // Prevent infinite loop
                                  if (page > 100) hasMore = false;
                                }

                                // Use fetched data for statistics
                                const allData = allWorkspaces;
                                const stats = workspaceStats?.summary || {};

                                // Calculate statistics from fetched data
                                const totalWorkspaces = allData.length;
                                const activeWorkspaces = allData.filter(w => w.status === 'active').length;
                                const suspendedWorkspaces = allData.filter(w => w.status === 'suspended').length;
                                const totalUsers = allData.reduce((sum, w) => sum + (w.memberCount || 0), 0);
                                const totalAgents = allData.reduce((sum, w) => sum + (w.agentCount || 0), 0);

                                // Group by plans
                                const planDistribution = allData.reduce((acc, w) => {
                                  const plan = w.currentSubscription?.planName || 'Free';
                                  acc[plan] = (acc[plan] || 0) + 1;
                                  return acc;
                                }, {});

                                // Create report content
                                const reportDate = new Date().toLocaleDateString();
                                const reportContent = `
WORKSPACE MANAGEMENT REPORT
Generated: ${reportDate}
================================

EXECUTIVE SUMMARY
-----------------
Total Workspaces: ${totalWorkspaces}
Active Workspaces: ${activeWorkspaces}
Paused Workspaces: ${suspendedWorkspaces}
Total Users: ${totalUsers}
Total Agents: ${totalAgents}

PLAN DISTRIBUTION
-----------------
${Object.entries(planDistribution).map(([plan, count]) => `${plan}: ${count} workspaces`).join('\n')}

TOP 10 WORKSPACES BY USERS
---------------------------
${allData.sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0)).slice(0, 10).map((w, i) =>
                                  `${i + 1}. ${w.name} - ${w.memberCount || 0} users`
                                ).join('\n')}

RECENT WORKSPACES (Last 30 days)
---------------------------------
${allData.filter(w => {
                                  const created = new Date(w.createdAt);
                                  const thirtyDaysAgo = new Date();
                                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                                  return created > thirtyDaysAgo;
                                }).map(w => `- ${w.name} (${new Date(w.createdAt).toLocaleDateString()})`).join('\n') || 'No new workspaces in the last 30 days'}

================================
End of Report
`;

                                // Download report as text file
                                const blob = new Blob([reportContent], { type: 'text/plain' });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `workspace_report_${new Date().toISOString().split('T')[0]}.txt`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                                EventBus.publish('success', `Report generated with ${totalWorkspaces} workspaces`);
                              } catch (error) {
                                console.error('Report generation error:', error);
                                EventBus.publish('error', 'Failed to generate report');
                              }
                            }}
                          >
                            <i className="fa fa-file-pdf mr-2"></i>
                            Generate Monthly Report
                          </button>

                          <div className="d-flex gap-2 mb-2">
                            <button
                              className='btn-style-border flex-fill'
                              onClick={() => {
                                setShowNotificationModal(true);
                              }}
                            >
                              <i className="fa fa-envelope mr-2"></i>
                              Send Bulk Notification
                            </button>
                            <button
                              className='btn-style-border'
                              onClick={() => setShowNotificationHistorySidebar(true)}
                              title="View notification history"
                            >
                              <i className="fa fa-history"></i>
                            </button>
                          </div>

                          <button
                            className='btn-style-border w-100'
                            onClick={() => {
                              dispatch(getWorkspaceStats());
                              dispatch(getAdminWorkspaces({ page: currentPage, search: searchTerm, status: statusFilter }));
                              EventBus.publish('success', 'Data refreshed successfully');
                            }}
                          >
                            <i className="fa fa-sync mr-2"></i>
                            Refresh All Data
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Workspace Templates - COMMENTED OUT */}
                  {/* <div className='col-lg-6 col-md-12'>
                    <div className='chart-area mt-3'>
                      <div className='table-area'>
                        <h4>Workspace Templates</h4>
                        <div className="mb-3">
                          <p className="text-muted small">Create and manage workspace templates for quick setup</p>
                          <button
                            className='btn-style-new mb-2'
                            onClick={() => {
                              setModalType('create');
                              setShowModal(true);
                              EventBus.publish('info', 'Template feature coming soon');
                            }}
                          >
                            <i className="fa fa-plus mr-2"></i>
                            Create Template
                          </button>
                        </div>
                        <div className="template-list">
                          <div className="d-flex justify-content-between align-items-center p-2 border rounded mb-2">
                            <div>
                              <strong>free Template</strong>
                              <small className="d-block text-muted">Basic features</small>
                            </div>
                            <div className='action-box' size="sm">
                              <button className='action-btn-table text-edit' color="info" size="sm"><i className="icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#A46497"></circle><path d="M13.5 17.375H10.5C7.785 17.375 6.625 16.215 6.625 13.5V10.5C6.625 7.785 7.785 6.625 10.5 6.625H11.5C11.705 6.625 11.875 6.795 11.875 7C11.875 7.205 11.705 7.375 11.5 7.375H10.5C8.195 7.375 7.375 8.195 7.375 10.5V13.5C7.375 15.805 8.195 16.625 10.5 16.625H13.5C15.805 16.625 16.625 15.805 16.625 13.5V12.5C16.625 12.295 16.795 12.125 17 12.125C17.205 12.125 17.375 12.295 17.375 12.5V13.5C17.375 16.215 16.215 17.375 13.5 17.375Z" fill="#A46497"></path><path d="M10.2497 14.845C9.94467 14.845 9.66467 14.735 9.45967 14.535C9.21467 14.29 9.10967 13.935 9.16467 13.56L9.37967 12.055C9.41967 11.765 9.60967 11.39 9.81467 11.185L13.7547 7.245C14.7497 6.25 15.7597 6.25 16.7547 7.245C17.2997 7.79 17.5447 8.345 17.4947 8.9C17.4497 9.35 17.2097 9.79 16.7547 10.24L12.8147 14.18C12.6097 14.385 12.2347 14.575 11.9447 14.615L10.4397 14.83C10.3747 14.845 10.3097 14.845 10.2497 14.845ZM14.2847 7.775L10.3447 11.715C10.2497 11.81 10.1397 12.03 10.1197 12.16L9.90467 13.665C9.88467 13.81 9.91467 13.93 9.98967 14.005C10.0647 14.08 10.1847 14.11 10.3297 14.09L11.8347 13.875C11.9647 13.855 12.1897 13.745 12.2797 13.65L16.2197 9.71C16.5447 9.385 16.7147 9.095 16.7397 8.825C16.7697 8.5 16.5997 8.155 16.2197 7.77C15.4197 6.97 14.8697 7.195 14.2847 7.775Z" fill="#A46497"></path><path d="M15.9248 10.915C15.8898 10.915 15.8548 10.91 15.8248 10.9C14.5098 10.53 13.4648 9.485 13.0948 8.17C13.0398 7.97 13.1548 7.765 13.3548 7.705C13.5548 7.65 13.7598 7.765 13.8148 7.965C14.1148 9.03 14.9598 9.875 16.0248 10.175C16.2248 10.23 16.3398 10.44 16.2848 10.64C16.2398 10.81 16.0898 10.915 15.9248 10.915Z" fill="#A46497"></path></svg>
                              </i>
                              </button>
                              <button className='action-btn-table text-danger' color="danger" size="sm"><i className="fa fa-trash"></i></button>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between align-items-center p-2 border rounded mb-2">
                            <div>
                              <strong>Starter Template</strong>
                              <small className="d-block text-muted">Basic Features and Starter Plan</small>
                            </div>
                            <div className='action-box' size="sm">
                              <button className='action-btn-table text-edit' color="info" size="sm"><i className="icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#A46497"></circle><path d="M13.5 17.375H10.5C7.785 17.375 6.625 16.215 6.625 13.5V10.5C6.625 7.785 7.785 6.625 10.5 6.625H11.5C11.705 6.625 11.875 6.795 11.875 7C11.875 7.205 11.705 7.375 11.5 7.375H10.5C8.195 7.375 7.375 8.195 7.375 10.5V13.5C7.375 15.805 8.195 16.625 10.5 16.625H13.5C15.805 16.625 16.625 15.805 16.625 13.5V12.5C16.625 12.295 16.795 12.125 17 12.125C17.205 12.125 17.375 12.295 17.375 12.5V13.5C17.375 16.215 16.215 17.375 13.5 17.375Z" fill="#A46497"></path><path d="M10.2497 14.845C9.94467 14.845 9.66467 14.735 9.45967 14.535C9.21467 14.29 9.10967 13.935 9.16467 13.56L9.37967 12.055C9.41967 11.765 9.60967 11.39 9.81467 11.185L13.7547 7.245C14.7497 6.25 15.7597 6.25 16.7547 7.245C17.2997 7.79 17.5447 8.345 17.4947 8.9C17.4497 9.35 17.2097 9.79 16.7547 10.24L12.8147 14.18C12.6097 14.385 12.2347 14.575 11.9447 14.615L10.4397 14.83C10.3747 14.845 10.3097 14.845 10.2497 14.845ZM14.2847 7.775L10.3447 11.715C10.2497 11.81 10.1397 12.03 10.1197 12.16L9.90467 13.665C9.88467 13.81 9.91467 13.93 9.98967 14.005C10.0647 14.08 10.1847 14.11 10.3297 14.09L11.8347 13.875C11.9647 13.855 12.1897 13.745 12.2797 13.65L16.2197 9.71C16.5447 9.385 16.7147 9.095 16.7397 8.825C16.7697 8.5 16.5997 8.155 16.2197 7.77C15.4197 6.97 14.8697 7.195 14.2847 7.775Z" fill="#A46497"></path><path d="M15.9248 10.915C15.8898 10.915 15.8548 10.91 15.8248 10.9C14.5098 10.53 13.4648 9.485 13.0948 8.17C13.0398 7.97 13.1548 7.765 13.3548 7.705C13.5548 7.65 13.7598 7.765 13.8148 7.965C14.1148 9.03 14.9598 9.875 16.0248 10.175C16.2248 10.23 16.3398 10.44 16.2848 10.64C16.2398 10.81 16.0898 10.915 15.9248 10.915Z" fill="#A46497"></path></svg>
                              </i>
                              </button>
                              <button className='action-btn-table text-danger' color="danger" size="sm"><i className="fa fa-trash"></i></button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> */}

                  {/* Admin Preferences Section - COMMENTED OUT */}
                  {/* <div className='col-lg-6 col-md-12'>
                    <div className='chart-area'>
                      <div className='table-area'>
                        <h4>Admin Preferences</h4>
                        <Form className="dashboard-form">
                          <FormGroup>
                            <Label>Items Per Page</Label>
                            <Input
                              type="select"
                              defaultValue="20"
                              onChange={(e) => {
                                localStorage.setItem('admin_items_per_page', e.target.value);
                                EventBus.publish('success', 'Preference saved');
                              }}
                            >
                              <option value="20">20 items</option>

                            </Input>
                          </FormGroup>

                          <FormGroup>
                            <Label>Date Format</Label>
                            <Input
                              type="select"
                              defaultValue="MM/DD/YYYY"
                              onChange={(e) => {
                                localStorage.setItem('admin_date_format', e.target.value);
                                EventBus.publish('success', 'Preference saved');
                              }}
                            >
                              <option value="MM/DD/YYYY">MM/DD/YYYY</option>

                            </Input>
                          </FormGroup>

                          <FormGroup>
                            <Label>Export Format</Label>
                            <Input
                              type="select"
                              defaultValue="csv"
                              onChange={(e) => {
                                localStorage.setItem('admin_export_format', e.target.value);
                                EventBus.publish('success', 'Preference saved');
                              }}
                            >
                              <option value="csv">CSV</option>
                              <option value="excel">Excel</option>

                            </Input>
                          </FormGroup>

                          <FormGroup>
                            <Label>Dashboard Refresh Rate</Label>
                            <Input
                              type="select"
                              defaultValue="manual"
                              onChange={(e) => {
                                localStorage.setItem('admin_refresh_rate', e.target.value);
                                EventBus.publish('success', 'Preference saved');
                              }}
                            >
                              <option value="manual">Manual</option>
                              <option value="30">Every 30 seconds</option>
                              <option value="60">Every 1 minute</option>
                              <option value="300">Every 5 minutes</option>
                            </Input>
                          </FormGroup>

                          <FormGroup check>
                            <Label check>
                              <Checkbox
                                defaultChecked
                                onChange={(e) => {
                                  localStorage.setItem('admin_show_tooltips', e.target.checked);
                                  EventBus.publish('success', 'Preference saved');
                                }}
                              />
                              Show tooltips
                            </Label>
                          </FormGroup>

                          <FormGroup check>
                            <Label check>
                              <Checkbox
                                defaultChecked
                                onChange={(e) => {
                                  localStorage.setItem('admin_confirm_actions', e.target.checked);
                                  EventBus.publish('success', 'Preference saved');
                                }}
                              />
                              Confirm before destructive actions
                            </Label>
                          </FormGroup>

                          <FormGroup check>
                            <Label check>
                              <Checkbox
                                onChange={(e) => {
                                  localStorage.setItem('admin_dark_mode', e.target.checked);
                                  EventBus.publish('success', 'Preference saved');
                                }}
                              />
                              Dark mode (Coming soon)
                            </Label>
                          </FormGroup>
                        </Form>
                      </div>
                    </div>*/}
                  <div className="col-lg-6 col-md-12">
                    <div className='chart-area'>
                      <div className='table-area'>
                        <h4>System Information</h4>
                        <div className="small">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Total Workspaces:</span>
                            <strong>{workspaceStats?.summary?.total || 0}</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Active Users:</span>
                            <strong>{workspaceStats?.summary?.totalUsers || 0}</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Total Agents:</span>
                            <strong>{workspaceStats?.summary?.totalAgents || 0}</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Last Updated:</span>
                            <strong>{new Date().toLocaleTimeString()}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
              {/* </div> */}
            </TabPane>

            {/* User Feedback Tab */}
            {accessType !== 'agency' && (
              <TabPane tabId="feedback">
                <div className='content-dashboard'>
                  <div className='title-area style-workshop'>
                    <div>
                      <h4>User Feedback Management</h4>
                      <p>View and manage all user feedback across the platform</p>
                    </div>
                  </div>

                  <Row className="mb-4">
                    <Col md="3">
                      <Card className="stats-card">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <p className="text-muted mb-1">Pending</p>
                              <h3 className="mb-0">{feedbackStats?.pending || 0}</h3>
                            </div>
                            <div className="stats-icon bg-warning">
                              <i className="fa fa-clock"></i>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md="3">
                      <Card className="stats-card">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <p className="text-muted mb-1">In Review</p>
                              <h3 className="mb-0">{feedbackStats?.inReview || 0}</h3>
                            </div>
                            <div className="stats-icon bg-info">
                              <i className="fa fa-eye"></i>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md="3">
                      <Card className="stats-card">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <p className="text-muted mb-1">Resolved</p>
                              <h3 className="mb-0">{feedbackStats?.resolved || 0}</h3>
                            </div>
                            <div className="stats-icon bg-success">
                              <i className="fa fa-check"></i>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md="3">
                      <Card className="stats-card">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <p className="text-muted mb-1">Total</p>
                              <h3 className="mb-0">{feedbackStats?.total || 0}</h3>
                            </div>
                            <div className="stats-icon bg-primary">
                              <i className="fa fa-comments"></i>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  <div className="table-container filters-table">
                    {/* Search and Filters */}
                    <div className='header-table-filters advanced-table-filters'>
                      <div className='left-area'>
                        <div className='filter-box'>
                          <div className='searchbox'>
                            <TextField
                              fullWidth
                              hiddenLabel
                              size="small"
                              name='search'
                              variant="standard"
                              placeholder="Search by Ticket ID or Title..."
                              value={feedbackSearchTerm}
                              onChange={(e) => setFeedbackSearchTerm(e.target.value)}
                            />
                            <i className='icon'>
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                                <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                              </svg>
                            </i>
                          </div>
                        </div>
                        <div className='filter-box'>
                          <Input
                            type="select"
                            value={feedbackStatusFilter}
                            onChange={(e) => setFeedbackStatusFilter(e.target.value)}
                          >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="in-review">In Review</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </Input>
                        </div>
                        <div className='filter-box'>
                          <Input
                            type="select"
                            value={feedbackTypeFilter}
                            onChange={(e) => setFeedbackTypeFilter(e.target.value)}
                          >
                            <option value="">All Types</option>
                            <option value="issue">Issue</option>
                            <option value="suggestion">Suggestion</option>
                            <option value="others">Others</option>
                          </Input>
                        </div>
                      </div>
                    </div>

                    <div className='table-area'>
                      {loading ? (
                        <div className="text-center my-4">
                          <CircularProgress size={30} />
                          <p className="text-muted mt-2">Loading feedback...</p>
                        </div>
                      ) : adminFeedback && adminFeedback.length > 0 ? (
                        <Table hover className="modern-table">
                          <thead>
                            <tr>
                              <th>Ticket ID</th>
                              <th>Type</th>
                              <th>User</th>
                              <th>Workspace</th>
                              <th>Title</th>
                              <th>Status</th>
                              <th>Priority</th>
                              <th>Date</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adminFeedback.map((feedback) => (
                              <tr key={feedback._id} style={{ cursor: 'pointer' }}>
                                <td><strong>{feedback.ticketId}</strong></td>
                                <td>
                                  <Badge
                                    color={
                                      feedback.type === 'issue'
                                        ? 'danger'
                                        : feedback.type === 'suggestion'
                                          ? 'success'
                                          : 'info'
                                    }
                                  >
                                    {feedback.type}
                                  </Badge>
                                </td>
                                <td>
                                  {feedback.userId?.firstName} {feedback.userId?.lastName}
                                  <br />
                                  <small className="text-muted">{feedback.userId?.email}</small>
                                </td>
                                <td>
                                  <span
                                    className="text-truncate d-inline-block"
                                    style={{ maxWidth: '150px' }}
                                  >
                                    {feedback.workspaceId?.name || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className="text-truncate d-inline-block"
                                    style={{ maxWidth: '200px' }}
                                  >
                                    {feedback.title}
                                  </span>
                                </td>
                                <td>
                                  <Badge
                                    color={
                                      feedback.status === 'pending'
                                        ? 'warning'
                                        : feedback.status === 'in-review'
                                          ? 'info'
                                          : feedback.status === 'resolved'
                                            ? 'success'
                                            : 'secondary'
                                    }
                                  >
                                    {feedback.status}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge
                                    color={
                                      feedback.priority === 'high' ||
                                        feedback.priority === 'critical'
                                        ? 'danger'
                                        : feedback.priority === 'medium'
                                          ? 'warning'
                                          : 'secondary'
                                    }
                                  >
                                    {feedback.priority}
                                  </Badge>
                                </td>
                                <td>
                                  <small>
                                    {new Date(feedback.createdAt).toLocaleDateString()}
                                  </small>
                                  <br />
                                  <small className="text-muted">
                                    {new Date(feedback.createdAt).toLocaleTimeString()}
                                  </small>
                                </td>
                                <td>
                                  <div className='action-box'>
                                    <button
                                      className='action-btn'
                                      color="secondary"
                                      title="View Details"
                                      onClick={() => {
                                        setSelectedFeedbackId(feedback._id);
                                        setShowFeedbackSidebar(true);
                                      }}
                                    >
                                      <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#323B43" /><path d="M12 14.165C10.805 14.165 9.83496 13.195 9.83496 12C9.83496 10.805 10.805 9.83501 12 9.83501C13.195 9.83501 14.165 10.805 14.165 12C14.165 13.195 13.195 14.165 12 14.165ZM12 10.585C11.22 10.585 10.585 11.22 10.585 12C10.585 12.78 11.22 13.415 12 13.415C12.78 13.415 13.415 12.78 13.415 12C13.415 11.22 12.78 10.585 12 10.585Z" fill="#323B43" /><path d="M12 16.51C10.12 16.51 8.34504 15.41 7.12504 13.5C6.59504 12.675 6.59504 11.33 7.12504 10.5C8.35004 8.58999 10.125 7.48999 12 7.48999C13.875 7.48999 15.65 8.58999 16.87 10.5C17.4 11.325 17.4 12.67 16.87 13.5C15.65 15.41 13.875 16.51 12 16.51ZM12 8.23999C10.385 8.23999 8.84004 9.20999 7.76004 10.905C7.38504 11.49 7.38504 12.51 7.76004 13.095C8.84004 14.79 10.385 15.76 12 15.76C13.615 15.76 15.16 14.79 16.24 13.095C16.615 12.51 16.615 11.49 16.24 10.905C15.16 9.20999 13.615 8.23999 12 8.23999Z" fill="#323B43" /></svg></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <div className="text-center my-4">
                          <p className="text-muted">No feedback found</p>
                        </div>
                      )}

                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        <span className="text-muted">
                          Showing {((feedbackPagination.page - 1) * feedbackPagination.limit) + 1} to{' '}
                          {Math.min(feedbackPagination.page * feedbackPagination.limit, feedbackPagination.total)} of{' '}
                          {feedbackPagination.total} entries
                        </span>
                      </div>
                      <Pagination>
                        <PaginationItem disabled={feedbackCurrentPage === 1}>
                          <PaginationLink
                            previous
                            onClick={() => setFeedbackCurrentPage(feedbackCurrentPage - 1)}
                          />
                        </PaginationItem>
                        {[...Array(feedbackPagination.pages)].map((_, index) => (
                          <PaginationItem key={index + 1} active={feedbackCurrentPage === index + 1}>
                            <PaginationLink onClick={() => setFeedbackCurrentPage(index + 1)}>
                              {index + 1}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem disabled={feedbackCurrentPage === feedbackPagination.pages}>
                          <PaginationLink
                            next
                            onClick={() => setFeedbackCurrentPage(feedbackCurrentPage + 1)}
                          />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  </div>
                </div>
              </TabPane>
            )}
          </TabContent>
        </div>
      </div>

      {/* Workspace Details Sidebar */}
      <WorkspaceSidebar
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        workspace={selectedWorkspace || selectedWorkspaceDetails?.workspace}
      />

      {/* Feedback Details Sidebar */}
      <FeedbackSidebar
        isOpen={showFeedbackSidebar}
        onClose={() => {
          setShowFeedbackSidebar(false);
          setSelectedFeedbackId(null);
        }}
        feedbackId={selectedFeedbackId}
        filters={{
          page: feedbackCurrentPage,
          search: feedbackSearchTerm,
          status: feedbackStatusFilter,
          type: feedbackTypeFilter
        }}
      />

      {/* Bulk Notification Modal */}
      <Modal isOpen={showNotificationModal} toggle={() => setShowNotificationModal(false)} size="lg" className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowNotificationModal(false)}>
          <h3>Send Bulk Notification</h3>
        </ModalHeader>
        <ModalBody>
          <Form className="dashboard-form mt-4">
            <FormGroup>
              <Label for="notificationSubject">Subject *</Label>
              <Input
                type="text"
                id="notificationSubject"
                value={notificationData.subject}
                onChange={(e) => setNotificationData({ ...notificationData, subject: e.target.value })}
                placeholder="Enter notification subject"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="notificationMessage">Message *</Label>
              <Input
                type="textarea"
                id="notificationMessage"
                value={notificationData.message}
                onChange={(e) => setNotificationData({ ...notificationData, message: e.target.value })}
                placeholder="Enter your notification message here..."
                rows="5"
                required
              />
            </FormGroup>

            {/* Workspace Selection Section */}
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Label>Select Sub-Workspaces *</Label>
                <div>
                  <button
                    size="sm"
                    color="link"
                    className="mr-2"
                    onClick={() => {
                      const allIds = adminWorkspaces.map(w => w._id);
                      setNotificationData({
                        ...notificationData,
                        selectedWorkspaces: allIds,
                        selectAll: true
                      });
                    }}
                  >
                    Select All
                  </button>
                  <button
                    size="sm"
                    color="link"
                    onClick={() => {
                      setNotificationData({
                        ...notificationData,
                        selectedWorkspaces: [],
                        selectAll: false
                      });
                    }}
                  >
                    Clear Selection
                  </button>
                </div>
              </div>

              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: '1px solid #dee2e6',
                borderRadius: '0.25rem',
                padding: '10px'
              }}>
                {adminWorkspaces && adminWorkspaces.length > 0 ? (
                  adminWorkspaces.map(workspace => (
                    <div key={workspace._id} className="worspace-chkbox">
                      <Checkbox
                        id={`workspace-${workspace._id}`}
                        checked={notificationData.selectedWorkspaces.includes(workspace._id)}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          let newSelected = [...notificationData.selectedWorkspaces];

                          if (isChecked) {
                            if (!newSelected.includes(workspace._id)) {
                              newSelected.push(workspace._id);
                            }
                          } else {
                            newSelected = newSelected.filter(id => id !== workspace._id);
                          }

                          setNotificationData({
                            ...notificationData,
                            selectedWorkspaces: newSelected,
                            selectAll: newSelected.length === adminWorkspaces.length
                          });
                        }}
                        style={{ marginRight: '10px' }}
                      />
                      <Label
                        for={`workspace-${workspace._id}`}
                        className="mb-0"
                        style={{ cursor: 'pointer' }}
                      >
                        <strong>{workspace.name}</strong>
                        {workspace.createdBy && (
                          <span className="text-muted ml-2">
                            ({workspace.createdBy.firstName} {workspace.createdBy.lastName} - {workspace.createdBy.email})
                          </span>
                        )}
                      </Label>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted">
                    No sub-workspaces available
                  </div>
                )}
              </div>

              {notificationData.selectedWorkspaces.length > 0 && (
                <Alert color="info" className="mt-3">
                  <small>
                    <strong>{notificationData.selectedWorkspaces.length}</strong> workspace{notificationData.selectedWorkspaces.length !== 1 ? 's' : ''} selected.
                    Email will be sent to unique workspace owners only (duplicates will be removed).
                  </small>
                </Alert>
              )}
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <button className='btn-style-border' onClick={() => {
            setShowNotificationModal(false);
            setNotificationData({ subject: '', message: '', selectedWorkspaces: [], selectAll: false });
          }}>
            Cancel
          </button>
          <button
            className='btn-style-new'
            onClick={async () => {
              if (!notificationData.subject || !notificationData.message) {
                EventBus.publish('error', 'Please fill in all required fields');
                return;
              }

              if (notificationData.selectedWorkspaces.length === 0) {
                EventBus.publish('error', 'Please select at least one workspace');
                return;
              }

              try {
                EventBus.publish('info', `Sending notifications to ${notificationData.selectedWorkspaces.length} selected workspace(s)...`);

                // Send notification to selected workspaces
                await handleBulkAction('send-notification', notificationData.selectedWorkspaces, {
                  subject: notificationData.subject,
                  message: notificationData.message
                });

                EventBus.publish('success', `Email notification sent to selected workspace owners`);
                setShowNotificationModal(false);
                setNotificationData({ subject: '', message: '', selectedWorkspaces: [], selectAll: false });
              } catch (error) {
                EventBus.publish('error', 'Failed to send notifications');
              }
            }}
          >
            Send Notification
          </button>
        </ModalFooter>
      </Modal>

      {/* Workspace Modal (for create and edit only) */}
      <Modal isOpen={showModal} toggle={() => setShowModal(false)} size="lg" className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal`}>
        <ModalHeader toggle={() => setShowModal(false)}>
          {modalType === 'create' && `Create New ${accessType === 'agency' ? 'Sub-Workspace' : 'Workspace'}`}
          {modalType === 'edit' && `Edit ${accessType === 'agency' ? 'Sub-Workspace' : 'Workspace'}`}
        </ModalHeader>
        <ModalBody>
          {(modalType === 'create' || modalType === 'edit') && (
            <Form className="dashboard-form ">
              {/* Agency Limit Warning */}
              {modalType === 'create' && accessType === 'agency' && workspaceStats?.['agencyInfo'] && (
                <Alert color={workspaceStats['agencyInfo']['remainingSlots'] <= 2 ? "warning" : "info"} className="mb-3">
                  <i className="fa fa-info-circle mr-2"></i>
                  You have <strong>{workspaceStats['agencyInfo']['remainingSlots']}</strong> sub-workspace slot{workspaceStats['agencyInfo']['remainingSlots'] !== 1 ? 's' : ''} remaining
                  out of your {workspaceStats['agencyInfo']['subWorkspaceLimit']} allowed by your {workspaceStats['agencyInfo']['planName']} plan.
                </Alert>
              )}
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="workspaceName">Workspace Name *</Label>
                    <Input
                      type="text"
                      id="workspaceName"
                      value={formData['name'] || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter workspace name"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="userEmail">User Email * <small className="text-muted">(Magic login link will be sent)</small></Label>
                    <Input
                      type="email"
                      id="userEmail"
                      value={formData['userEmail'] || ''}
                      onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
                      placeholder={`Enter ${accessType === 'agency' ? 'client' : 'user'} email (e.g., client@company.com)`}
                      required
                    />
                    <small className="text-info">
                      <i className="fa fa-info-circle mr-1"></i>
                      A login link will be sent to this email for direct access
                    </small>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="firstName">First Name *</Label>
                    <Input
                      type="text"
                      id="firstName"
                      value={formData['firstName'] || ''}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Enter first name"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="lastName">Last Name</Label>
                    <Input
                      type="text"
                      id="lastName"
                      value={formData['lastName'] || ''}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Enter last name"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="workspaceStatus">Status</Label>
                    <Input
                      type="select"
                      id="workspaceStatus"
                      value={formData['status'] || ''}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="suspended">Paused</option>
                      <option value="inactive">Inactive</option>
                      <option value="trial">Trial</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="workspacePlan">
                      Plan
                      {modalType === 'create' && accessType === 'agency' && <span className="text-danger"> *</span>}
                    </Label>
                    {!hasStripeConfigured && plansMessage && accessType === 'agency' ? (
                      <Alert color="warning" className="mb-0">
                        <i className="fa fa-exclamation-triangle mr-2"></i>
                        {plansMessage}
                        <br />
                        <small>Please go to the billing section to validate your Stripe key first.</small>
                      </Alert>
                    ) : (
                      <Input
                        type="select"
                        id="workspacePlan"
                        value={formData['plan'] || ''}
                        onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                        disabled={workspacePlans.length === 0}
                      >
                        <option value="">
                          {workspacePlans.length === 0 ? 'No plans available' : 'Select a plan'}
                        </option>
                        {workspacePlans.map(plan => (
                          <option key={plan._id} value={plan._id}>
                            {plan.displayText || `${plan.displayName} - $${plan.price}/${plan.interval === 'year' ? 'year' : 'mo'}`}
                          </option>
                        ))}
                      </Input>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label for="workspaceDescription">Description</Label>
                    <Input
                      type="textarea"
                      id="workspaceDescription"
                      value={formData['description'] || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={`Enter ${accessType === 'agency' ? 'client company' : 'workspace'} description`}
                      rows="3"
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Hide Billing Toggle - Show for agency access */}
              {((modalType === 'create' || modalType === 'edit') && accessType === 'agency') && (
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData['hideBilling'] || false}
                            onChange={(e) => setFormData({ ...formData, hideBilling: e.target.checked })}
                            color="primary"
                          />
                        }
                        label="Hide billing from client (activate plan without payment)"
                      />
                      <div style={{ marginTop: '8px' }}>
                        <small style={{ color: '#6c757d', display: 'block' }}>
                          <i className="fa fa-info-circle mr-1"></i>
                          When enabled, the client workspace will have full access to the selected plan. Billing is visible to the client, but payment will be handled manually by the agency.
                        </small>
                      </div>

                      {/* Date Pickers - Show when toggle is ON */}
                      {formData['hideBilling'] && (
                        <Row className="mt-3">
                          <Col md={6}>
                            <FormGroup>
                              <Label>Current Period Start *</Label>
                              <Input type="date" value={formData['currentPeriodStart'] || ''}
                                onChange={(e) => setFormData({ ...formData, currentPeriodStart: e.target.value })} required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Current Period End *</Label>
                              <Input type="date" value={formData['currentPeriodEnd'] || ''}
                                onChange={(e) => setFormData({ ...formData, currentPeriodEnd: e.target.value })} required
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              )}
            </Form>
          )}

          {/* View modal section removed - now using sidebar */}
        </ModalBody>
        <ModalFooter>
          <button className='btn-style-border' onClick={() => setShowModal(false)}>
            Cancel
          </button>
          {(modalType === 'create' || modalType === 'edit') && (
            <button
              className='btn-style-new'
              onClick={handleSaveWorkspace}
              disabled={
                (modalType === 'create' && accessType === 'agency' && !formData['plan']) ||
                (formData['hideBilling'] && (!formData['currentPeriodStart'] || !formData['currentPeriodEnd']))
              }
              style={{
                opacity: ((modalType === 'create' && accessType === 'agency' && !formData['plan']) || (formData['hideBilling'] && (!formData['currentPeriodStart'] || !formData['currentPeriodEnd']))) ? 0.6 : 1,
                cursor: ((modalType === 'create' && accessType === 'agency' && !formData['plan']) || (formData['hideBilling'] && (!formData['currentPeriodStart'] || !formData['currentPeriodEnd']))) ? 'not-allowed' : 'pointer'
              }}
            >
              {modalType === 'create' ? `Create ${accessType === 'agency' ? 'Sub-Workspace' : 'Workspace'}` : 'Save Changes'}
            </button>
          )}
        </ModalFooter>
      </Modal>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)} centered className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal`}>
        <ModalHeader toggle={() => setShowConfirmModal(false)}>
          <h3><i className={`fa ${confirmData.confirmClass === 'btn-danger' ? 'fa-exclamation-triangle text-danger' : 'fa-exclamation-circle text-warning'} mr-2`}></i>
            {confirmData.title}</h3>
        </ModalHeader>
        <ModalBody>
          <div className="mb-3 mt-4">
            <p className="mb-3"><strong>{confirmData.message}</strong></p>

            {confirmData.details && confirmData.details.length > 0 && (
              <div className="mb-3">
                <p className="mb-2">This will:</p>
                <ul className="ps-3">
                  {confirmData.details.map((detail, index) => (
                    <li key={index} className="mb-1">{detail}</li>
                  ))}
                </ul>
              </div>
            )}

            {confirmData.note && (
              <div className={`alert ${confirmData.confirmClass === 'btn-danger' ? 'alert-danger' : 'alert-warning'} mb-0`}>
                <i className={`fa ${confirmData.confirmClass === 'btn-danger' ? 'fa-warning' : 'fa-info-circle'} me-2`}></i>
                {confirmData.note}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn-style-border" color="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </button>
          <button
            className={'btn-style-new'}
            onClick={confirmData.onConfirm}
          >
            {confirmData.confirmText || 'Confirm'}
          </button>
        </ModalFooter>
      </Modal>

      {/* Notification History Sidebar */}
      <NotifyHistory
        isOpen={showNotificationHistorySidebar}
        onClose={() => setShowNotificationHistorySidebar(false)}
      />

      {/* Exit Impersonation Floating Button */}
      <ExitImpersonate />
    </>
  );
};

export default WorkspacesManagement;